import {
  BANK_FILE_STAGE_LABELS,
  BRANCHES_TERMINALES,
  CONSULTABLE_DOCUMENT_CODES,
  ETAPES_DOSSIER,
  type EtapeDescriptor,
  type EtapeDossier,
  fullStepperPath,
} from "@hakili/domain-types";
import {
  coverageGateDetailsSchema,
  type DealerAccessFactsAnswer,
  IN_PLACE_ACTIONS,
  STAGE_ACTIONS,
} from "@hakili/contracts";
import { formatDate, formatDateTime, formatMoney, t } from "@hakili/i18n";
import { legWords, type SettlementLineView } from "./settlements.js";
import { GuardedButton, Modal, Stepper } from "@hakili/ui";
import {
  ArrowLeft,
  Bot,
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  MessageCircle,
  MessageSquare,
  MinusCircle,
  Phone,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { CopyableLink } from "./copyable-link.js";
import { useAnalysisPolling } from "./first-pass-polling.js";
import { DealerAccessFactsPanel } from "./dealer-access-facts.js";
import { FirstPassSection, type FirstPassReadingView, type SynthesisView } from "./first-pass.js";
import {
  EmploymentVerificationSection,
  type EmploymentVerificationState,
  type VerificationBody,
} from "./employment-verification-panel.js";
import { SubmissionPanel, type SubmissionOptionsView } from "./submission-panel.js";
import {
  UnderwritingSection,
  type AnalysisChainView,
  type ConsoleUnderwritingView,
} from "./underwriting-panel.js";

import type { Vue } from "../shell/views.js";

/**
 * `round CN-A`, `CN-A.1`: **the file, `hakili-console.jsx`'s `DossierScreen`
 * and `PanneauEtape`.**
 *
 * ## The order is the record's, and it differs from the orders
 *
 * `WF-CON-04` step 2: *"Stage panel first, because the first question in front
 * of a dossier is not what the client said, it is where the dossier stands and
 * what to do now."* The round's orders listed the synthesis first. **The
 * record's text wins**, and the divergence is flagged here and in the addendum.
 *
 * ## The stage panels are in the AMENDED order, and the drawing is not
 *
 * The 10 September pass touched only Expertises; the drawing's `intake` panel
 * still offers « Démarrer la collecte » → `collecte`, which is the order the
 * founder's decision of 10 September replaced. Under EXPERT FIRST, `intake` is
 * the buyer's stage: Hakili waits on his money and his launch, and **this
 * console advances nothing there** , the panel says where the fee stands, in
 * words, and offers the closure alone. The drawing wins where no recorded
 * decision says otherwise, and here one does.
 *
 * ## What is a dash, and what is greyed
 *
 * The synthesis and « Lecture de Hakili » are `intelligence`'s and no door
 * answers them , the card stands with the drawing's title and says which round
 * carries it. The composer, the reminders and « Contacter le vendeur » are
 * `messaging`'s and it sends nothing , greyed with the reason and the round in
 * the title (`I19`). A dash is never a paragraph and never a number (`R81`).
 */

export interface DossierDetail {
  readonly id: string;
  readonly reference: string;
  readonly clientName: string;
  readonly clientPhone: string;
  readonly vehicleLabel: string;
  readonly sellerName: string;
  readonly stage: string;
  readonly ownerUserId: string | null;
  readonly ownerName: string | null;
  readonly amountFinanced: number;
  /**
   * `U-1`, `M0 (a)` (console 101): the file's intention, the door's own column. On a cash file the
   * amount is the price the buyer commits to (02 §2.3.1's `montant`, T-2b (a)), so the header's word
   * is « Prix du véhicule » and never « Montant financé »; absent (a file read before the field), the
   * financed word stands (R81).
   */
  readonly intention?: "financement" | "comptant";
  /** `L-1`, `M1`: the buyer's own deposit and term from the file; `null` prints a dash (R81). */
  readonly depositPct?: number | null;
  readonly durationMonths?: number | null;
  /** Round `L-2`, `M3`: the bank the buyer retained, the door's; absent before. */
  readonly mainBankName?: string | null;
  readonly attente: string;
  /**
   * `G3-3`, the founder's decision of 17 October 2026 (WAIT IN COLLECTE): the instant the buyer sent his file while
   * the seller's pièces were not all received; `attente` then reads « En attente des pièces du vendeur ». `null` before.
   */
  readonly clientSentAt?: string | null;
  /** `C7.4`: the civility the chargé read off the identity document, `null` before. */
  readonly clientGender?: "f" | "m" | null;
  readonly identityHolderName?: string | null;
  /** `P1-REST`: the stage a closed file left (the detail read's `stageBefore`); `null` while it is live. */
  readonly stageBefore?: string | null;
  /** The api's own: days in the stage, and how late against `DELAI_ETAPE`, or `null`. */
  readonly daysInStage?: number;
  readonly lateBy?: number | null;
}

export interface FeeView {
  readonly clientSettledOn: string | null;
  readonly clientAmount: number;
  readonly clientStatus: string;
}

export interface PieceView {
  readonly code: string;
  readonly label: string;
  readonly status: string;
  readonly source: string | null;
  /** The WIRE's own field names , `auteur`, `recuLe`, `relanceLe` are what the door answers (`09-01`). */
  readonly auteur: string | null;
  readonly recuLe: string | null;
  readonly relanceLe: string | null;
  /** The chargé's note at registration (« photo prise au guichet »). */
  readonly note?: string | null;
  /** `C7.3`: « lien de dépôt envoyé » once a link has gone out, the door's own words. */
  readonly lienDepot?: string | null;
  /** `C7.1`: the chargé's reason when the pièce was rejected; the row awaits a new deposit. */
  readonly rejectionReason?: string | null;
  /** `OD-37` R7: the day the founder retired this piece (`YYYY-MM-DD`), else `null`. */
  readonly retiredOn?: string | null;
  readonly rejectedAt?: string | null;
  /**
   * `VISITE-SANS-OBJET` (17 October 2026): the pièce reads « sans objet » (`status: "sansObjet"`) , the chargé's reason,
   * who marked it and when; `null` for every other pièce. Neither received nor missing.
   */
  readonly notApplicable?: { readonly reason: string; readonly by: string; readonly at: string } | null;
  /** The control this row offers, named by the door (« Relancer sur cette pièce » / « Renvoyer le lien »). */
  readonly action?: string | null;
  /** `S4-6`: `action`'s mirror , the control toward the vendor, for a vendeur pièce alone. */
  readonly vendorAction?: string | null;
  /** `C7.6`: who supplies the pièce , the buyer, or the seller through the office. */
  readonly suppliedBy?: "client" | "vendeur";
  /** `C7.6`: for a seller's pièce, the channel the office received it by. */
  readonly receptionChannel?: string | null;
  /**
   * `A-DLR`, Arc B (+D): for a seller's pièce the dealership supplied itself , `vehicule` (filled from the vehicle's
   * papers) or `concessionnaire` (deposited through his own door). `null` for any other road.
   */
  readonly provenance?: "vehicule" | "concessionnaire" | "emise" | null;
  /** `S4-9`: when the chargé opened the pièce itself, `null` while unopened. */
  readonly consultedAt?: string | null;
  /** `S4-5-MULTI-FILE`: true for a pièce whose own label asks for several documents. */
  readonly multiFile?: boolean;
  /** `PIECES-COUNT-ENFORCE`: for a multi-file pièce, the months it asks for (the count its files' declared months must add up to); `null` for every other pièce. */
  readonly expectedFiles?: number | null;
  /** `PIECES-COUNT-ENFORCE`: the months the files received so far cover (the sum of their declared months); `null` for every other pièce. The pièce is complete when this reaches `expectedFiles`. */
  readonly coveredMonths?: number | null;
}

export interface JournalLine {
  readonly id: string;
  readonly body: string;
  readonly channel: string;
  readonly occurredAt: string;
  readonly isAutomatic: boolean;
}

export interface GridFamily {
  readonly code: string;
  readonly title: string;
  readonly isEliminating: boolean;
  readonly isCheckable: boolean;
  readonly criteria: readonly { code: string; label: string }[];
}

export interface ReportEntry {
  readonly observed: readonly string[];
  readonly estimatedValueAmount: number;
  readonly observations: string;
  readonly receptionChannel: string;
  /** Round `C6` (`OD-33`): the cabinet's own paper, the document of record , REQUIRED. */
  readonly paper: File;
}

/**
 * `AA-3d2` (D-1018-AA2): what the chargée asks the client to complete when she sends a file back to the collecte: her
 * reworded lines (each tied to one of HIS pièces, or free) and her free note. Both optional at the door.
 */
export interface ReturnAsk {
  readonly items: readonly { readonly pieceCode: string | null; readonly text: string }[];
  readonly note: string | null;
}

/** `COVERAGE-READ-CHECK`: a line of the return form, tied to one pièce (`pieceCode`) or free (`""`). */
export interface ReturnPrefillLine {
  readonly pieceCode: string;
  readonly text: string;
}

export interface DossierProps {
  readonly vue: Vue;
  /**
   * `P1-CNW-064` (D-1031-18): the dealer-access facts, `GET /dossiers/{id}/dealer-access-facts` under `voirEtRelancerDossiers`. Handed ONLY to a seat that holds the right; where it is absent the panel
   * is not drawn and the door is never asked.
   */
  readonly dealerAccess?: {
    readonly dossierId: string;
    readonly read: (dossierId: string) => Promise<DealerAccessFactsAnswer>;
  };
  /** The signed-in person's id, for `WF-CON-04`'s `peutAvancer`. */
  readonly meId: string;
  readonly dossier: DossierDetail;
  /** The actions the STAGE offers, the door's own list; the screen invents none. */
  readonly actions: readonly string[];
  readonly fee: FeeView | null;
  readonly verdict: string | null;
  /**
   * `FX-3`, `M2 (d)` (`D6`): the chargé's own gesture on an awaited transfer , the ping, never a
   * confirmation. Absent on a screen composed without it, and the panel then says the state and offers
   * nothing, which is what it did before this movement.
   */
  readonly flagControl?: (() => Promise<boolean>) | undefined;

  /** `L-4`, `M3 (b)` (WF-CON-12): the last filter run's failing verdict, its own words , the exit exists only then. */
  readonly filterFailure?: string | null;
  /** `AA-3a`: the filter's last computation of THIS stay in `analyse` (the eligible banks it found); `null` while none stands. */
  readonly filterComputation?: { readonly eligibleCount: number } | null;
  /** `AA-3a2` (D-1018-1): « Refuser » , the reason is the chargée's own, required, 300 characters at most. */
  readonly onRefuse?: ((reason: string) => void) | undefined;
  /** `T-1c`, `M4`: the stages the file passed without walking (the parked insurance); the stepper reads them as bypassed. */
  readonly stagesBypassed?: readonly string[];
  /** `round C5-2`, `M3`: the report's provenance and the cabinet's own paper, as the file door answers them. */
  readonly report?: ReportProvenance | null;
  /** Round `C6`, `M4`: the report of record read , the two documents per version, the versions kept, the request standing. */
  readonly reading?: ReportReading | null;
  readonly onRequestCorrection?: (reason: string) => void;
  /** `LANE-E`, `M0` (b): « Régénérer la lecture Hakili », for a report whose derived sheet is missing. */
  readonly onRegenerateSheet?: () => void;
  readonly pieces: readonly PieceView[];
  readonly manquants: number;
  readonly journal: readonly JournalLine[];
  /** `L-1b`, `M5` (b): the banks' answers, bank by bank, `GET /dossiers/{id}/submissions`. */
  readonly submissions?: readonly SubmissionLine[];
  /** Round `L-2`, `M3` (B10): the manual recording's door and the re-read after it. */
  readonly recordOffer?: DossierDoors["recordOffer"];
  readonly onRecorded?: () => void;
  /** Round `L-3b`, `M2` (M0 (d), WF-CON-14 step 4): the manual refusal's door, the closed list, the declaration. */
  readonly recordRefusal?: DossierDoors["recordRefusal"];
  readonly refusalReasons?: readonly { readonly code: string; readonly label: string }[];
  readonly onAllRefused?: (() => void) | undefined;
  /** Round `L-3`, `M2` (WF-CON-04 step 3): the board's two doors; « Relance envoyée » reads the pièces' rows (R-B). */
  readonly onChaseForBank?: ((documentCode: string) => void) | undefined;
  readonly onResubmit?: ((submissionId: string) => void) | undefined;
  /** The demand `payments` linked to this file, or `null` before the launch. */
  readonly demandId: string | null;
  readonly grid: readonly GridFamily[] | null;
  readonly assignable: readonly { id: string; fullName: string }[];
  readonly busy: boolean;
  readonly refusal: string | null;
  /** `M0(b)`: a part's refusal, by the part's name, rendered where the part is. */
  readonly partRefusals?: Readonly<Record<string, string>>;
  readonly onBack: () => void;
  /** `T-1b`, `M2` (WF-CON-18): Hakili's four conditions and the bank's five as information; the ticks by their doors. */
  /** `K-1`, `M3 (d)`: the ledger of days on a held file, `null` while the car is under no hard lock. */
  readonly holds?: HoldLedgerView | null | undefined;
  readonly conditions?: readonly DossierConditionView[] | null;
  readonly bankConditions?: BankConditionsView | null;
  /** `T-2` (WF-CON-18 steps 5 and 6): the settlement in two legs on this file, `payments`' fact; `null` while none. */
  readonly settlement?: SettlementLineView | null;
  readonly onLiftCondition?: ((cle: string, how: "lift" | "reopen") => void) | undefined;
  /**
   * `U-1`, `M3`: « Lecture de Hakili » , the first pass's reading by its door (`null` when no run
   * stands, R81's sentence; `undefined` where the screen was not handed the door), the chargée's
   * two acts by hers.
   */
  readonly firstPass?: FirstPassReadingView | null | undefined;
  /** `U-3`, `M1`: the synthesis by its door; `undefined` where the door was not handed, `null` where it answered none. */
  readonly synthesis?: SynthesisView | null | undefined;
  readonly onFirstPassAct?:
    ((key: string, act: "confirmee" | "corrigee" | "manuelle", value: string | null) => void) | undefined;
  /**
   * `P3`: the typed fields of one pièce, sent in sequence by the per-field door; answers each refused field's words by
   * its key (a field not in the answer is saved). `undefined` where the door was not handed: no input is drawn.
   */
  readonly onFirstPassEnter?:
    | ((entries: readonly { key: string; value: string }[]) => Promise<Readonly<Record<string, string>>>)
    | undefined;
  /**
   * Round 11a (`OD-37`, D-1010-3): « Analyse de Hakili » , the console run by its door (`undefined` where the door
   * was not handed, `null` where no run stands) and the re-read while it runs. `AA-3c`: and the chain behind the ONE
   * button « Lancer l'analyse Hakili » , where it stands, the button's own answer, the folded history , with the press
   * that starts it (`undefined` where the door was not handed).
   */
  readonly underwriting?: ConsoleUnderwritingView | null | undefined;
  readonly analysis?: AnalysisChainView | null | undefined;
  /**
   * `AA-4b` (`D-1018-AA2`): at Prêt, what the chargée chooses from , the banks the filter retains and excludes, whether the gesture is
   * open , and her press, `POST /dossiers/{id}/submissions` with the banks she ticked (`undefined` where the door was not handed).
   */
  readonly submissionOptions?: SubmissionOptionsView | null | undefined;
  readonly onSubmitToBanks?: ((bankIds: readonly string[]) => void) | undefined;
  readonly onLaunchAnalysis?: (() => void) | undefined;
  /**
   * Round 10a (`OD-37`, workflow §1.5, WF-CON-13, decision D-1010-3): the chargée's employment verification , the act
   * that rules and the send gate's state by its door (`undefined` where the door was not handed, `null` where it
   * answered none), and her door that records it. « Soumettre aux banques » waits on the act, with no bypass.
   */
  readonly verification?: EmploymentVerificationState | null | undefined;
  readonly onRecordVerification?: ((body: VerificationBody) => Promise<void>) | undefined;
  readonly onAdvance: (action: string) => void;
  readonly onClose: (reason: string, code: string) => void;
  /** `A-RECOLLECTE`: « Remettre en collecte » , the reason is required, 300 characters at most. */
  readonly onReturnToCollecte?: ((reason: string, ask?: ReturnAsk) => void) | undefined;
  /**
   * `COVERAGE-READ-CHECK` (`P0-DOS-066`): the lines the return form opens with after the submission door refused a multi-file pièce Hakili read
   * short of its months: one per short pièce, the sentence the CLIENT reads as the door served it (`details.pieces[].clientSentence`).
   * The browser composes none (D-1020-7); she edits them. `null`/absent: the form opens empty, as ever.
   */
  readonly returnPrefill?: readonly ReturnPrefillLine[] | null | undefined;
  /**
   * `COVERAGE-READ-CHECK`: the door's sentence for the refused send, drawn as a warn notice under « Soumettre aux banques » and above the form
   * (it is the cause; a refusal, not a failed read, so no « Réessayer »). `null`/absent: nothing drawn.
   */
  readonly sendRefusal?: string | null | undefined;
  /**
   * `PRET-SEND-FILTER-GUARD` (`P0-DOS-074`): the door's sentence when a field act of the reading is refused from `soumis` onward
   * (`409 stage_guard_failed`, `details.guard = "lecture_figee"`), drawn as served in the reading's own place. `null`/absent: nothing drawn.
   */
  readonly fieldActRefusal?: string | null | undefined;
  /**
   * `READING-REQUIRED` (RR-2, `P0-DOS-071`): the door's sentence when « Valider et passer à Prêt » was refused for a reading never launched
   * (`409 stage_guard_failed`, `details.guard = "reading_not_launched"`), drawn as served beside the gesture. `null`/absent: nothing drawn.
   */
  readonly readingRefusal?: string | null | undefined;
  /** `READING-REQUIRED`, C9: reads the file's parts again (the failed read's « Réessayer »). */
  readonly onRetryRead?: (() => void) | undefined;
  readonly onReassign: (userId: string, reason: string) => void;
  /** `round N+1`: no `author` parameter , the staff name is resolved server side, never sent. */
  readonly onFileOnBehalf: (
    documentCode: string,
    channel: string,
    note: string,
    files: readonly File[],
    declaredMonths?: readonly number[],
  ) => void;
  readonly onFileReport: (entry: ReportEntry) => void;
  readonly onGoExpertises: () => void;
  /**
   * `C7.3`: the deposit links minted THIS sitting, by pièce code, absolute on
   * the client app's origin. Answered once by the door and held here only:
   * a credential the office copies into WhatsApp, never re-read, never shown
   * unless asked.
   */
  readonly links: ReadonlyMap<string, string>;
  readonly onChase: (documentCode: string) => void;
  readonly onChaseAll: () => void;
  /** `S4-6`: the mirror , mints the SAME shape of link, the vendor named instead of the client. */
  readonly onChaseSeller: (documentCode: string) => void;
  /** `C7.1`: a received pièce rejected with its reason; the row awaits again. */
  readonly onReject: (documentCode: string, reason: string) => void;
  /** `VISITE-SANS-OBJET`: the visite technique marked « sans objet » with a reason; and the undo, with a reason. */
  readonly onMarkNotApplicable: (documentCode: string, reason: string) => void;
  readonly onUndoNotApplicable: (documentCode: string, reason: string) => void;
  /** `C7.4`: the civility and the holder's name, read off the identity document. */
  readonly onVerifyIdentity: (gender: "f" | "m", holderName: string) => void;
  /** `S4-9`: opens a pièce , a presigned URL, `null` when it could not be obtained. */
  readonly onConsult: (documentCode: string) => Promise<string | null>;
}

const CHIP_BY_STAGE = new Map<string, string>([
  ["intake", "bg-gray-100 text-gray-600"],
  ["collecte", "bg-amber-50 text-amber-700"],
  ["expertise", "bg-sky-50 text-sky-700"],
  ["analyse", "bg-indigo-50 text-indigo-700"],
  ["pret", "bg-violet-50 text-violet-700"],
  ["soumis", "bg-emerald-50 text-emerald-700"],
  ["offre", "bg-teal-50 text-teal-700"],
  ["assurance", "bg-cyan-50 text-cyan-700"],
  ["conditions", "bg-blue-50 text-blue-700"],
  ["decaisse", "bg-emerald-100 text-emerald-800"],
  ["refuse", "bg-rose-50 text-rose-600"],
  ["refuseBanque", "bg-rose-50 text-rose-600"],
  ["sansSuite", "bg-gray-100 text-gray-600"],
]);

/** The three the door accepts (`CANAUX_RECEPTION`), keyed by the WIRE value. */
const CHANNELS = new Map<string, string>([
  ["whatsapp", "console.dossier.canalWhatsapp"],
  ["agence", "console.dossier.canalAgence"],
  ["courriel", "console.dossier.canalCourriel"],
]);

const STAGES = [...ETAPES_DOSSIER, ...BRANCHES_TERMINALES];
const stageOf = (id: string): EtapeDescriptor | undefined => STAGES.find((stage) => stage.id === id);
const isTerminal = (id: string): boolean => BRANCHES_TERMINALES.some((branch) => branch.id === id);

const DASH = t("common.valeur.tiret");
/** `P45`: the journal shows its latest lines; « Voir tout l'historique (N) » opens the rest in place. */
const JOURNAL_SHOWN = 3;
const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));
/** R24 (c): a fingerprint is quoted SHORT on the screen, the journal's own twelve characters; the full one is the document's. */
const shortHash = (sha: string | null): string => (sha === null ? DASH : `${sha.slice(0, 12)}…`);

// `L-1b`, R24 (d): a date the console prints is French, as everywhere else on the platform (6.5.3).
const day = (iso: string | null): string => (iso === null ? DASH : formatDate(new Date(iso)));
/** `P45` (walk P5): a journal line carries its date AND its time. */
const dateTime = (iso: string): string => formatDateTime(new Date(iso));

/**
 * `P45` (walk P5): the journal's channels, by the wire value the door sends , the chargée's word and
 * the icon beside it. « systeme » is Hakili. A value the screen has no word for shows as the door sent it.
 */
const JOURNAL_CHANNELS = new Map<string, { readonly label: string; readonly Icon: LucideIcon }>([
  ["systeme", { label: t("console.dossier.journalCanalSysteme"), Icon: Bot }],
  ["whatsapp", { label: t("console.dossier.canalWhatsapp"), Icon: MessageCircle }],
  ["sms", { label: t("console.dossier.journalCanalSms"), Icon: MessageSquare }],
  ["appel", { label: t("console.dossier.journalCanalAppel"), Icon: Phone }],
  ["courriel", { label: t("console.dossier.canalCourriel"), Icon: Mail }],
  ["visite", { label: t("console.dossier.journalCanalVisite"), Icon: MapPin }],
  ["comite", { label: t("console.dossier.journalCanalComite"), Icon: Users }],
]);
const channelWord = (channel: string): string => JOURNAL_CHANNELS.get(channel)?.label ?? channel;

function JournalMeta({ line }: { readonly line: JournalLine }): React.JSX.Element {
  const Icon = JOURNAL_CHANNELS.get(line.channel)?.Icon ?? MessageSquare;
  return (
    <p className="flex items-center gap-1.5 text-xs text-gray-500">
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {channelWord(line.channel)} · {dateTime(line.occurredAt)}
    </p>
  );
}

/** The wire's action words, from `contracts` , DATA, never copy (`09-01`). */
const ACTION = STAGE_ACTIONS;

/**
 * The drawing's « Prochaine étape : … » line, from the table and never typed.
 *
 * `round W-13`, `M4` (`CN-H`'s `M0 (f)`, carried): **at `expertise`, once a
 * report is in, the line follows the VERDICT** , the collecte on « conforme »
 * (the table's own next stage), the seller's answer on the price on « écart de
 * prix », the closure with the buyer's refund on « non conforme ». The
 * founder's finding of 13 September: « Prochaine étape : collecte des pièces »
 * beside a verdict forbidding it.
 */
const nextStepWords = (
  stage: string,
  verdict: string | null = null,
  ended: { readonly filterFailed: boolean; readonly offersAllEnded: boolean } = {
    filterFailed: false,
    offersAllEnded: false,
  },
): string => {
  const current = stageOf(stage);
  if (current === undefined) return DASH;
  if (isTerminal(stage)) return current.label;
  // `L-5`, `M3` (the ended file's words): the line reads the state, never a step the state contradicts.
  if (stage === "analyse" && ended.filterFailed)
    return `${t("console.dossier.prochaineEtape")} ${t("console.dossier.prochaineEtapeFiltreEchoue")}`;
  if (stage === "soumis" && ended.offersAllEnded)
    return `${t("console.dossier.prochaineEtape")} ${t("console.dossier.prochaineEtapeOffresExpirees")}`;
  if (stage === "expertise" && verdict === "ecartprix")
    return `${t("console.dossier.prochaineEtape")} ${t("console.dossier.prochaineEtapeEcartPrix")}`;
  if (stage === "expertise" && verdict === "nonconforme")
    return `${t("console.dossier.prochaineEtape")} ${t("console.dossier.prochaineEtapeNonConforme")}`;
  // `T-1`, `M2` (OD-40, found at the shooting): at « Offre » the next step is the conditions, the
  // insurance at the client's charge , never the parked placement the table's order would name.
  if (stage === "offre")
    return `${t("console.dossier.prochaineEtape")} ${t("console.dossier.prochaineEtapeOffreConditions")}`;
  const at = ETAPES_DOSSIER.findIndex((one) => one.id === stage);
  const next = ETAPES_DOSSIER[at + 1];
  if (next === undefined) return t("console.dossier.derniereEtape");
  return `${t("console.dossier.prochaineEtape")} ${next.label.charAt(0).toLowerCase()}${next.label.slice(1)}.`;
};

/**
 * `AA-3a2` (`DX-006`, D-1018-6) and `P1-REST`: **the shared `Stepper`, and nothing drawn here.**
 *
 * The page used to draw its own row of dots in a strip that scrolled sideways. `@hakili/ui`'s `Stepper` wraps instead,
 * so every stage of the path stands in view at any width. `P1-REST`: the path is the domain's `fullStepperPath` , the
 * stages travelled, the current one, then the stages still AHEAD of a live file in grey; on a terminal branch nothing
 * is ahead (`I20`), and the path ends at the stage the file left (`stageBefore`). A stage the file passed without
 * walking (`T-1c`, the parked insurance) keeps its « sans objet » beside its name.
 */
function StageStepper({
  stage,
  stageBefore,
  bypassed,
}: {
  readonly stage: string;
  /** `P1-REST`: where a closed file stood when it entered its branch. */
  readonly stageBefore?: string | null | undefined;
  /** `T-1c`, `M4` (T-1b's booking (3)): a bypassed stage reads as bypassed, never lit as passed. */
  readonly bypassed: readonly string[];
}): React.JSX.Element {
  const { travelled, ahead } = fullStepperPath(stage as EtapeDossier, stageBefore);
  const node = (one: EtapeDescriptor): { id: string; label: string; terminal?: true } => ({
    id: one.id,
    label: bypassed.includes(one.id) ? `${one.court} (${t("console.dossier.etapeSansObjet")})` : one.court,
    ...(isTerminal(one.id) ? { terminal: true as const } : {}),
  });
  return (
    <div data-testid="dossier-etapes">
      <Stepper path={travelled.map(node)} ahead={ahead.map(node)} />
    </div>
  );
}

/**
 * Round `C6`, `M4` (`OD-31`, `OD-33`): **the report of record, read from the
 * file's page.** « Ouvrir le rapport » (the cabinet's own paper), « Lecture
 * Hakili » (the derived sheet), each with its fingerprint; the versions kept,
 * each with its two documents; « Demander une correction au cabinet » , the
 * chargé's reason to the cabinet, never a correction by Hakili's hand. A
 * missing sheet is a greyed control with its reason (A47); a standing request
 * greys the ask. The links are signed for this read and printed nowhere.
 */
export interface ReadableDocument {
  readonly documentId: string;
  readonly fileName: string;
  readonly contentHash: string;
  readonly url: string;
}
export interface ReportVersionReading {
  readonly version: number;
  readonly reportedAt: string;
  readonly document: ReadableDocument | null;
  readonly sheet: ReadableDocument | null;
  readonly supersededAt: string | null;
}
export interface ReportReading {
  readonly reportId: string;
  readonly demandId: string;
  readonly cabinetName: string;
  readonly version: number;
  readonly current: ReportVersionReading;
  readonly versions: readonly ReportVersionReading[];
  readonly correctionRequested: {
    readonly reason: string;
    readonly requestedAt: string;
    readonly byName: string | null;
  } | null;
}

/**
 * `SWEEP-AA` (D-1019-3): **a line whose action is done shows its status , and no dead button.** A check, a plain gray
 * sentence (it may carry the date and the reference); never an amber reason, never a greyed control. `data-row-state`
 * is what the layout contract reads to hold every console route to it.
 */
function DoneLine(props: { readonly testId: string; readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <p
      data-testid={props.testId}
      data-row-state="done"
      className="flex items-center gap-2 text-sm text-gray-700"
    >
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
      <span>{props.children}</span>
    </p>
  );
}

function DocumentLink(props: {
  readonly document: ReadableDocument | null;
  readonly label: string;
  readonly absent: string;
  readonly testId: string;
  readonly tone: "primary" | "plain";
}): React.JSX.Element {
  const classes =
    props.tone === "primary"
      ? "rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700"
      : "rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700";
  if (props.document === null) {
    return (
      <button
        type="button"
        disabled
        title={props.absent}
        data-testid={props.testId}
        className={`${classes} opacity-40`}
      >
        {props.label}
      </button>
    );
  }
  return (
    <a
      href={props.document.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={props.testId}
      className={classes}
    >
      {props.label}
    </a>
  );
}

function ReportReadingSection(props: {
  readonly reading: ReportReading;
  readonly busy: boolean;
  readonly mayAsk: boolean;
  readonly onRequestCorrection: (reason: string) => void;
  readonly onRegenerateSheet: () => void;
}): React.JSX.Element {
  const { reading } = props;
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const requested = reading.correctionRequested;
  return (
    <section
      data-testid="dossier-rapport-lecture"
      className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
    >
      <p className="text-sm font-semibold text-gray-900">{t("console.dossier.rapportDeRecordTitre")}</p>
      <p className="mt-0.5 text-xs text-gray-500" data-testid="dossier-rapport-lecture-cabinet">
        {reading.cabinetName}
      </p>
      <p
        className="mt-1 break-all text-sm text-gray-500 tabular-nums"
        data-testid="dossier-rapport-lecture-empreinte"
      >
        {fill("console.dossier.rapportEmpreinteCourte", {
          version: String(reading.current.version),
          sha: shortHash(reading.current.document?.contentHash ?? null),
          date: day(reading.current.reportedAt),
        })}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <DocumentLink
          document={reading.current.document}
          label={t("console.dossier.ouvrirRapport")}
          absent={t("console.dossier.papierAbsent")}
          testId="dossier-rapport-ouvrir"
          tone="primary"
        />
        <DocumentLink
          document={reading.current.sheet}
          label={t("console.dossier.lectureHakili")}
          absent={t("console.dossier.lectureAbsente")}
          testId="dossier-rapport-lecture-hakili"
          tone="plain"
        />
        {reading.current.sheet !== null ? null : props.mayAsk ? (
          <button
            type="button"
            data-testid="dossier-regenerer-lecture"
            disabled={props.busy}
            onClick={props.onRegenerateSheet}
            className="rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 disabled:opacity-40"
          >
            {t("console.dossier.regenererLecture")}
          </button>
        ) : null}
        {reading.current.sheet === null ? null : (
          <span
            className="break-all text-sm text-gray-500 tabular-nums"
            data-testid="dossier-rapport-lecture-sheet-empreinte"
          >
            {fill("console.dossier.lectureEmpreinteCourte", {
              sha: shortHash(reading.current.sheet.contentHash),
              date: day(reading.current.reportedAt),
            })}
          </span>
        )}
      </div>

      <p className="mt-3 text-xs font-medium text-gray-700">{t("console.dossier.versionsTitre")}</p>
      {reading.versions.length === 0 ? (
        <p className="mt-0.5 text-xs text-gray-500" data-testid="dossier-rapport-versions-aucune">
          {t("console.dossier.versionsAucune")}
        </p>
      ) : (
        <ul className="mt-1 space-y-1.5" data-testid="dossier-rapport-versions">
          {reading.versions.map((kept) => (
            <li
              key={kept.version}
              className="flex flex-wrap items-center gap-2 text-xs text-gray-500"
              data-testid={`dossier-rapport-version-${String(kept.version)}`}
            >
              <span className="break-all tabular-nums">
                {fill("console.dossier.versionLigne", {
                  n: String(kept.version),
                  date: formatDate(new Date(kept.reportedAt)),
                  sha: kept.document?.contentHash ?? t("common.valeur.tiret"),
                })}
              </span>
              <DocumentLink
                document={kept.document}
                label={t("console.dossier.ouvrirRapport")}
                absent={t("console.dossier.papierAbsent")}
                testId={`dossier-rapport-version-${String(kept.version)}-ouvrir`}
                tone="plain"
              />
              <DocumentLink
                document={kept.sheet}
                label={t("console.dossier.lectureHakili")}
                absent={t("console.dossier.lectureAbsente")}
                testId={`dossier-rapport-version-${String(kept.version)}-lecture`}
                tone="plain"
              />
            </li>
          ))}
        </ul>
      )}

      {requested === null ? null : (
        <p
          className="mt-3 rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-700"
          data-testid="dossier-correction-demandee"
        >
          {fill("console.dossier.correctionDemandee", {
            date: formatDate(new Date(requested.requestedAt)),
            name: requested.byName ?? t("common.valeur.tiret"),
            reason: requested.reason,
          })}
        </p>
      )}
      {asking ? (
        <div className="mt-3" data-testid="dossier-correction-form">
          <textarea
            rows={2}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            placeholder={t("console.dossier.correctionMotifAide")}
            data-testid="dossier-correction-motif"
            className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={props.busy || reason.trim() === ""}
              onClick={() => {
                props.onRequestCorrection(reason.trim());
                setAsking(false);
                setReason("");
              }}
              data-testid="dossier-correction-envoyer"
              className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-medium text-white disabled:opacity-40"
            >
              {t("console.dossier.correctionEnvoyer")}
            </button>
            <button
              type="button"
              onClick={() => {
                setAsking(false);
              }}
              data-testid="dossier-correction-annuler"
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-700"
            >
              {t("console.dossier.correctionAnnuler")}
            </button>
          </div>
        </div>
      ) : requested !== null ? (
        // The request stands: the line says so (D-1019-3). Anyone who reads the lecture sees it.
        <div className="mt-3">
          <DoneLine testId="dossier-demander-correction-fait">
            {t("console.dossier.correctionDejaDemandee")}
          </DoneLine>
        </div>
      ) : !props.mayAsk ? null : (
        <button
          type="button"
          data-testid="dossier-demander-correction"
          disabled={props.busy}
          onClick={() => {
            setAsking(true);
          }}
          className="mt-3 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {t("console.dossier.demanderCorrection")}
        </button>
      )}
    </section>
  );
}

export interface ReportProvenance {
  readonly reportId: string;
  readonly demandId: string;
  readonly entryMode: string;
  readonly authorLabel: string;
  readonly receptionChannel: string | null;
  readonly reportedAt: string;
  readonly visaByName: string | null;
  readonly visaAt: string | null;
  readonly documentId: string | null;
  /** Round `C6`: the fingerprint, the version, the derived sheet, the versions kept, Hakili's open request. */
  readonly documentSha256?: string | null;
  readonly version?: number;
  readonly derivedSheetDocumentId?: string | null;
  readonly authorName?: string | null;
  readonly versions?: readonly {
    readonly version: number;
    readonly reportedAt: string;
    readonly documentId: string | null;
    readonly documentSha256: string | null;
    readonly derivedSheetDocumentId: string | null;
    readonly supersededAt: string;
  }[];
  readonly correctionRequested?: {
    readonly reason: string;
    readonly requestedAt: string;
    readonly byName: string | null;
  } | null;
}

/**
 * `round C5-2`, `M3`, WF-CON-34's amendment: the report's provenance in words
 * , `cabinet · espace · <responsable> · <date>` when the cabinet sent it under
 * its visa; the team's own entry with its reception channel otherwise.
 */
const provenanceWords = (report: ReportProvenance): string => {
  const date = formatDate(new Date(report.visaAt ?? report.reportedAt));
  if (report.entryMode === "cabinet" && report.visaByName !== null) {
    return `cabinet · espace · ${report.visaByName} · ${date}`;
  }
  if (report.entryMode === "cabinet") return `cabinet · ${report.authorLabel} · ${date}`;
  // Round `C6`, stage 1: « cabinet · <canal> · <date> · <chargé> » , the paper
  // is the cabinet's, the typed entry a TRANSCRIPTION carrying the chargé's name.
  if (report.documentId !== null && typeof report.authorName === "string" && report.authorName !== "") {
    return `cabinet · ${report.receptionChannel ?? t("common.valeur.tiret")} · ${date} · ${fill(
      "console.dossier.provenanceTranscription",
      { name: report.authorName },
    )}`;
  }
  return `${t("console.dossier.provenanceEquipe")} · ${report.receptionChannel ?? t("common.valeur.tiret")} · ${date}`;
};

export function Dossier(props: DossierProps): React.JSX.Element {
  const { dossier } = props;
  // `U-1`, `M3` and `G3-1(d)`: the first pass's panel stands from the collecte on, and wherever a run is known.
  // `AA-3a2` (D-1018-1): the Collecte screen is human work only , the reading is the Analyse screen's.
  const lecturePanelDrawn =
    props.firstPass !== undefined &&
    dossier.stage !== "collecte" &&
    (props.firstPass !== null || dossier.stage === "analyse");
  /**
   * `WF-CON-04`'s own guard, to the letter: admin, responsable, or the OWNING
   * chargé. Any other viewer reads. The door enforces the same thing on its
   * side; this decides only whether to draw the controls at all.
   */
  const canAct =
    props.vue === "admin" ||
    props.vue === "resp" ||
    (props.vue === "collecte" && dossier.ownerUserId === props.meId);
  const [open, setOpen] = useState<
    "clore" | "reattribuer" | "televerser" | "rapport" | "remettre" | "refuser" | null
  >(null);
  // `COVERAGE-READ-CHECK`: the door refused the send for a short pièce; the return form opens at once, prefilled with what the door served.
  const prefilled = props.returnPrefill ?? null;
  useEffect(() => {
    if (prefilled !== null) setOpen("remettre");
  }, [prefilled]);
  const [conversation, setConversation] = useState(false);
  const [journalAll, setJournalAll] = useState(false);

  return (
    <div data-testid="dossier">
      <button
        type="button"
        onClick={props.onBack}
        data-testid="dossier-retour"
        className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("console.dossier.retour")}
      </button>

      {/* `WF-CON-04` step 1: the header , client, chip, reference, vehicle,
          seller, amount. */}
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{dossier.clientName}</h1>
            <span
              data-testid="dossier-etape"
              className={
                "rounded-full px-2.5 py-1 text-xs font-medium " + (CHIP_BY_STAGE.get(dossier.stage) ?? "")
              }
            >
              {stageOf(dossier.stage)?.label ?? dossier.stage}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {dossier.reference} · {dossier.vehicleLabel} · {t("console.dossier.vendeur")} {dossier.sellerName}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500" data-testid="dossier-montant-libelle">
            {dossier.intention === "comptant"
              ? t("console.dossier.prixVehicule")
              : t("console.dossier.montantFinance")}
          </p>
          <p className="text-2xl font-semibold tabular-nums">{formatMoney(dossier.amountFinanced)}</p>
        </div>
      </div>

      {/* `DX-001` (D-1018-6): ONE column under 1024 px, so the follow-up column's buttons are never
          wider than the window; two thirds and one third from `lg`. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          {/* `WF-CON-04` step 2: the STAGE PANEL FIRST. */}
          <section
            data-testid="dossier-panneau"
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
          >
            {/* The drawing's stepper, over the domain's own stage table , the
                stages walked, the current one ringed, the terminal branch in
                rose after the stage it left. */}
            <StageStepper
              stage={dossier.stage}
              stageBefore={dossier.stageBefore}
              bypassed={props.stagesBypassed ?? []}
            />
            {/* `K-1`, `M3 (d)`: the ledger card mounts after this panel, see `HoldLedgerCard`. */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <h2 className="text-sm font-semibold">{stageOf(dossier.stage)?.label ?? dossier.stage}</h2>
              {/* `W-13`, `M4`: the chip follows the report once one is in ,
                  « Rapport reçu · … » , and the door's word otherwise. */}
              <span
                data-testid="dossier-etat-chip"
                className={
                  isTerminal(dossier.stage)
                    ? "rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-600"
                    : "rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700"
                }
              >
                {dossier.stage === "expertise" && props.verdict === "conforme"
                  ? t("console.dossier.rapportRecuConforme")
                  : dossier.stage === "expertise" && props.verdict === "ecartprix"
                    ? t("console.dossier.rapportRecuEcartPrix")
                    : dossier.stage === "expertise" && props.verdict === "nonconforme"
                      ? t("console.dossier.rapportRecuNonConforme")
                      : dossier.attente}
              </span>
              {/* `WF-CON-04` step 2: the stagnation, the api's own `lateBy`. */}
              {dossier.lateBy !== null &&
              dossier.lateBy !== undefined &&
              dossier.daysInStage !== undefined ? (
                <span
                  data-testid="dossier-stagnation"
                  className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-600"
                >
                  {dossier.daysInStage === 1
                    ? t("console.dossier.stagnationJourUn")
                    : `${String(dossier.daysInStage)} ${t("console.dossier.stagnationJours")}`}{" "}
                  {dossier.daysInStage - dossier.lateBy === 1
                    ? t("console.dossier.stagnationAttenduUn")
                    : `${String(dossier.daysInStage - dossier.lateBy)} ${t("console.dossier.stagnationAttendus")}`}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-gray-500" data-testid="dossier-prochaine-etape">
              {nextStepWords(dossier.stage, props.verdict, {
                filterFailed: (props.filterFailure ?? null) !== null,
                offersAllEnded:
                  (props.submissions ?? []).length > 0 &&
                  (props.submissions ?? []).every((one) => endWordsOf(one, true) !== null),
              })}
            </p>

            {canAct ? null : (
              <p
                data-testid="dossier-lecture-seule"
                className="mt-3 rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600"
              >
                {t("console.dossier.lectureSeule").replace(
                  "{charge}",
                  dossier.ownerName ?? t("console.dossier.personnePourLInstant"),
                )}
              </p>
            )}

            {/* `L-3b`, `M2` (`P1-DOS-008`, WF-CON-12, WF-CLI-21): a terminal branch , zero
                future stages, the branch's explanation (the domain's own field) and the
                drawing's sentence; the chip above reads « Dossier clos », never « Terminé » (I20). */}
            {isTerminal(dossier.stage) ? (
              <div className="mt-3" data-testid="dossier-terminal">
                <p
                  className="text-xs leading-relaxed text-gray-600"
                  data-testid="dossier-terminal-explication"
                >
                  {BRANCHES_TERMINALES.find((branch) => branch.id === dossier.stage)?.explication ?? ""}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-gray-500" data-testid="dossier-terminal-clos">
                  {t("console.dossier.closSentence")}
                </p>
              </div>
            ) : null}

            <StagePanel {...props} canAct={canAct} open={open} setOpen={setOpen} />

            {/* Round `L-4`, `M2` (WF-CON-19, B14): a closed file keeps its board , the study
                withdrawn and the offer declined by the closure are read from the rows, under the
                terminal panel, with no control: the rows ended with the file. */}
            {isTerminal(dossier.stage) &&
            (props.submissions ?? []).length > 0 &&
            props.onResubmit !== undefined &&
            props.onChaseForBank !== undefined ? (
              <div className="mt-3">
                <BankFollowUp
                  submissions={props.submissions ?? []}
                  pieces={props.pieces}
                  busy={props.busy}
                  onChase={props.onChaseForBank}
                  onResubmit={props.onResubmit}
                />
              </div>
            ) : null}

            {props.refusal === null ? null : (
              <p role="alert" data-testid="dossier-refus" className="mt-3 text-sm text-rose-600">
                {props.refusal}
              </p>
            )}
          </section>
          {/* `K-1`, `M3 (d)`: « Où passe le temps », the ledger of days on a held file. */}
          <HoldLedgerCard
            holds={props.holds}
            closed={isTerminal(dossier.stage) || dossier.stage === "decaisse"}
          />

          {/* Round `C6`, `M4`: the report of record, wherever the file stands once a report does. */}
          {props.reading === undefined || props.reading === null ? null : (
            <ReportReadingSection
              reading={props.reading}
              busy={props.busy}
              mayAsk={props.vue === "admin" || props.vue === "resp"}
              onRequestCorrection={props.onRequestCorrection ?? (() => undefined)}
              onRegenerateSheet={props.onRegenerateSheet ?? (() => undefined)}
            />
          )}

          {/* `WF-CON-04` step 4: the synthesis, `intelligence`'s. In words. */}
          {/* `U-1`, `M3`: the first pass's reading under the stage panel, from the collecte on , the
              drawing's block name, the register's placement (flagged), every word the door's. */}
          {lecturePanelDrawn ? (
            <FirstPassSection
              reading={props.firstPass}
              canAct={canAct && !isTerminal(dossier.stage) && props.onFirstPassAct !== undefined}
              busy={props.busy}
              refusal={props.partRefusals?.["lecture"] ?? null}
              actRefusal={props.fieldActRefusal ?? null}
              onAct={(key, act, value) => {
                props.onFirstPassAct?.(key, act, value);
              }}
              pieces={props.pieces.map((one) => ({
                code: one.code,
                recuLe: one.recuLe,
                auteur: one.auteur,
                provenance: one.provenance,
              }))}
              onEnter={props.onFirstPassEnter}
              onOpenDocument={props.onConsult}
            />
          ) : null}

          {/* Round 11a (`OD-37`, D-1010-3): « Analyse de Hakili » , the console run, register first, then the grade card
              with its operands, then the avis; the control « Lancer l'analyse Hakili » is offered at `analyse`. */}
          {props.underwriting !== undefined &&
          (props.underwriting !== null || dossier.stage === "analyse") ? (
            <UnderwritingSection
              view={props.underwriting}
              analysis={props.analysis}
              atAnalysis={dossier.stage === "analyse"}
              canAct={canAct && !isTerminal(dossier.stage) && props.onLaunchAnalysis !== undefined}
              busy={props.busy}
              refusal={props.partRefusals?.["analyse"] ?? null}
              onLaunch={() => {
                props.onLaunchAnalysis?.();
              }}
              pieces={props.pieces.map((one) => ({
                code: one.code,
                label: one.label,
                received: one.status === "recue",
              }))}
              onOpenDocument={canAct ? props.onConsult : undefined}
              // `P2`: her three decisions close the Analyse card, once its results exist and not before.
              decisions={
                dossier.stage === "analyse" && canAct && !isTerminal(dossier.stage)
                  ? (mode) => (
                      <AnalyseDecisions
                        {...props}
                        canAct={canAct}
                        open={open}
                        setOpen={setOpen}
                        without={mode === "without"}
                      />
                    )
                  : undefined
              }
            />
          ) : null}

          {/* Round 10a (workflow §1.5): « Vérification d'emploi » , the chargée's own act, from the collecte on; the
              send at `pret` waits on it (the stage panel greys « Soumettre aux banques » with the door's sentence). */}
          {props.verification !== undefined &&
          props.verification !== null &&
          (props.verification.verification !== null ||
            dossier.stage === "collecte" ||
            dossier.stage === "analyse" ||
            dossier.stage === "pret") ? (
            <EmploymentVerificationSection
              state={props.verification}
              canAct={
                canAct &&
                !isTerminal(dossier.stage) &&
                props.onRecordVerification !== undefined &&
                (dossier.stage === "collecte" || dossier.stage === "analyse" || dossier.stage === "pret")
              }
              busy={props.busy}
              onRecord={(body) => props.onRecordVerification?.(body) ?? Promise.resolve()}
            />
          ) : null}

          <section
            data-testid="dossier-synthese"
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
          >
            <h2 className="text-sm font-semibold">{t("console.dossier.syntheseTitre")}</h2>
            {/* `L-1`, `M1` (C3-B's ruling (ii)): the buyer's own terms, the FILE's
                two columns and never a slider's default; a dash where the file
                was born before them (R81). */}
            <div className="mt-2" data-testid="dossier-synthese-conditions">
              <p className="text-xs font-medium text-gray-500">{t("console.dossier.conditionsTitre")}</p>
              <p className="mt-0.5 text-sm text-gray-900">
                {fill("console.dossier.conditionsAcheteur", {
                  apport:
                    dossier.depositPct === null || dossier.depositPct === undefined
                      ? DASH
                      : fill("console.dossier.apportPct", { apport: String(dossier.depositPct) }),
                  duree:
                    dossier.durationMonths === null || dossier.durationMonths === undefined
                      ? DASH
                      : fill("console.dossier.dureeMois", { duree: String(dossier.durationMonths) }),
                })}
              </p>
            </div>
            {/* `U-3`, `M1` (WF-CON-04 step 4, `P3-ITL-008`): the counters and the three to five derived points,
                each with its tone, by the door; the R81 sentence only where the door was not handed. */}
            {props.synthesis === undefined ? (
              <p className="mt-2 text-sm text-gray-500">{t("console.dossier.syntheseMots")}</p>
            ) : props.synthesis === null ? (
              <p className="mt-2 text-sm text-gray-500" data-testid="dossier-synthese-absente">
                {t("console.dossier.syntheseAbsente")}
              </p>
            ) : (
              <SynthesisBlock
                synthesis={props.synthesis}
                onOpenConversation={() => {
                  setConversation(true);
                }}
              />
            )}
          </section>

          <DocumentsSection {...props} canAct={canAct} open={open} setOpen={setOpen} />

          {/* The journal, newest first (`P45`, walk P5): the door orders it oldest first and the screen
              turns it round. The conversation is the side panel (`WF-CON-04` step 7), opened from the
              synthesis' « Messages » (or from here where the synthesis is unread), never a thread on the page. */}
          <section
            data-testid="dossier-journal"
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t("console.dossier.journalTitre")}</h2>
              {/* `P45`: a route that exists stays callable. « Messages » is the one door while the
                  synthesis renders; where it cannot be read, the journal's header keeps this one. */}
              {typeof props.synthesis === "object" && props.synthesis !== null ? null : (
                <button
                  type="button"
                  data-testid="dossier-ouvrir-conversation"
                  onClick={() => {
                    setConversation(true);
                  }}
                  className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                >
                  <MessageSquare className="h-4 w-4" aria-hidden="true" />
                  {t("console.dossier.ouvrirConversation")}
                </button>
              )}
            </div>
            {/* `M0(b)`: the journal's own refusal, where the journal is. */}
            {props.partRefusals?.["journal"] === undefined ? null : (
              <p role="alert" data-testid="dossier-journal-refus" className="mt-2 text-xs text-rose-600">
                {props.partRefusals["journal"]}
              </p>
            )}
            {props.journal.length === 0 ? (
              <p className="mt-2 text-xs text-gray-500">{t("console.dossier.journalVide")}</p>
            ) : (
              <>
                <ul className="mt-2 divide-y divide-gray-100">
                  {[...props.journal]
                    .reverse()
                    .slice(0, journalAll ? undefined : JOURNAL_SHOWN)
                    .map((line) => (
                      <li key={line.id} className="py-2">
                        <JournalMeta line={line} />
                        <p className="text-sm text-gray-700">{line.body}</p>
                      </li>
                    ))}
                </ul>
                {journalAll || props.journal.length <= JOURNAL_SHOWN ? null : (
                  <button
                    type="button"
                    data-testid="dossier-journal-tout"
                    onClick={() => {
                      setJournalAll(true);
                    }}
                    className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {fill("console.dossier.journalTout", { n: String(props.journal.length) })}
                  </button>
                )}
              </>
            )}
          </section>

          {props.dealerAccess === undefined ? null : (
            <DealerAccessFactsPanel dossierId={props.dealerAccess.dossierId} read={props.dealerAccess.read} />
          )}
        </div>

        {/* `WF-CON-04` step 9: the right column. */}
        <div className="flex min-w-0 flex-col gap-4">
          <section
            data-testid="dossier-suivi"
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
          >
            <h2 className="text-sm font-semibold">{t("console.dossier.suiviTitre")}</h2>
            <dl className="mt-2 flex flex-col gap-1.5 text-xs">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">{t("console.dossier.suiviCharge")}</dt>
                <dd className="font-medium text-gray-900" data-testid="dossier-charge">
                  {dossier.ownerName ?? DASH}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">{t("console.dossier.suiviTelephone")}</dt>
                <dd className="font-medium tabular-nums text-gray-900">{dossier.clientPhone}</dd>
              </div>
            </dl>
            {/* `WF-CON-20`: the reassignment is the administration's and the
                responsable's , the door refuses everyone else, and the control
                is not drawn for them. */}
            {props.vue === "admin" || props.vue === "resp" ? (
              <div className="mt-3">
                {open === "reattribuer" ? (
                  <ReassignForm {...props} />
                ) : (
                  <button
                    type="button"
                    data-testid="dossier-reattribuer"
                    disabled={props.busy || isTerminal(dossier.stage)}
                    onClick={() => {
                      setOpen("reattribuer");
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    {t("console.dossier.reattribuer")}
                  </button>
                )}
              </div>
            ) : null}
          </section>

          {/* `G3-1(d)`: the placeholder says the reading has no door yet. It is drawn only where the
              panel above is NOT , the panel shows a run, or says none stands, and a sentence that
              denies the door beside a run is false. */}
          {lecturePanelDrawn ? null : (
            <section
              data-testid="dossier-lecture"
              className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
            >
              <h2 className="text-sm font-semibold">{t("console.dossier.lectureTitre")}</h2>
              <p className="mt-2 text-sm text-gray-500" data-testid="dossier-lecture-mots">
                {t("console.dossier.lectureMots")}
              </p>
            </section>
          )}

          {/* `T-1`, `M2` (WF-CON-17): the insurance card, the pilot's two sentences. */}
          <InsuranceCard stage={dossier.stage} />

          {/* The side « Actions » card is gone (`D-1019-2`): both of its buttons , « Contacter le vendeur »,
              « Programmer un rendez-vous de dépôt » , were unbuilt, greyed on every stage; a card of dead controls is
              not drawn. It returns with the first control that works. */}
        </div>
      </div>

      {conversation ? (
        <ConversationPanel
          journal={props.journal}
          onClose={() => {
            setConversation(false);
          }}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------ the stage panel

type PanelProps = DossierProps & {
  readonly canAct: boolean;
  readonly open: "clore" | "reattribuer" | "televerser" | "rapport" | "remettre" | "refuser" | null;
  readonly setOpen: (
    open: "clore" | "reattribuer" | "televerser" | "rapport" | "remettre" | "refuser" | null,
  ) => void;
};

/** `CONSOLE-MONTHS-QUESTION`: the words of a number of months, the client's own (« 1 mois », « 2 mois », « 3 mois »). */
const monthsWords = (count: number): string =>
  count === 1
    ? t("console.dossier.moisUn")
    : count === 2
      ? t("console.dossier.moisDeux")
      : count === 3
        ? t("console.dossier.moisTrois")
        : fill("console.dossier.moisPlusieurs", { n: String(count) });

const missingWords = (count: number): string =>
  `${String(count)} ${count === 1 ? t("console.dossier.pieceManquanteUne") : t("console.dossier.piecesManquantesPlusieurs")}`;

function StagePanel(props: PanelProps): React.JSX.Element | null {
  const { dossier, canAct } = props;
  if (!canAct || isTerminal(dossier.stage)) return null;

  return (
    <div className="mt-3 flex flex-col gap-2">
      {dossier.stage === "intake" ? (
        <IntakePanel
          fee={props.fee}
          vue={props.vue}
          {...(props.flagControl === undefined ? {} : { onFlag: props.flagControl })}
          onOpenSequestre={props.onGoExpertises}
        />
      ) : null}

      {dossier.stage === "expertise" ? <ExpertisePanel {...props} /> : null}

      {/* `L-1b`, `M5` (b), WF-CON-14 step 1: one line per bank with its answer , the
          bank's own act by the event (« Offre reçue le <date> »), or the refusal
          with its motive kept on the bank's side. The chargé's manual controls
          below stand as the stage machine's actions, for a bank off the platform. */}
      {dossier.stage === "soumis" ? (
        <SoumisPanel
          submissions={props.submissions ?? []}
          refusalReasons={props.refusalReasons ?? []}
          recordOffer={props.recordOffer}
          recordRefusal={props.recordRefusal}
          onRecorded={props.onRecorded}
          onAllRefused={props.onAllRefused}
          busy={props.busy}
        />
      ) : null}
      {/* Round `L-3`, `M2`: the board, under the stage panel, whenever submissions exist (WF-CON-04 step 3). */}
      {props.onResubmit !== undefined && props.onChaseForBank !== undefined ? (
        <BankFollowUp
          submissions={props.submissions ?? []}
          pieces={props.pieces}
          busy={props.busy}
          onChase={props.onChaseForBank}
          onResubmit={props.onResubmit}
        />
      ) : null}

      {/* Round `L-2`, `M3`: the file at « Offre » , the checklist reads the buyer's
          acceptance (the event moved the file), the placement is the insurance round's. */}
      {dossier.stage === "offre" ? (
        <OffrePanel dossier={dossier} busy={props.busy} onAdvance={props.onAdvance} />
      ) : null}

      {/* `AA-3a2`: the Collecte screen , human work only, one primary action. */}
      {dossier.stage === "collecte" ? <CollectePanel {...props} /> : null}

      {/* `AA-3a2`: the Analyse screen , the results, then her three decisions; nothing moves without her. */}
      {dossier.stage === "analyse" ? <AnalysePanel {...props} /> : null}

      {/* `AA-4b` (`D-1018-AA2`): at Prêt the chargée chooses the banks herself, the filter's recommendation beside each. */}
      {dossier.stage === "pret" &&
      props.submissionOptions !== undefined &&
      props.submissionOptions !== null &&
      props.onSubmitToBanks !== undefined ? (
        <SubmissionPanel
          options={props.submissionOptions}
          busy={props.busy}
          onSubmit={(bankIds) => {
            props.onSubmitToBanks?.(bankIds);
          }}
        />
      ) : null}

      {/* Every other stage: the door's own action list, each a control calling
          `/advance`, refused in the door's words when its guard says no. The
          `collecte` stage's « Envoyer à l'analyse » is that list's one entry. */}
      {/* `T-1b`, `M2` (WF-CON-18; `hakili-console.jsx`, `CONDITIONS_PREALABLES`): the four conditions as the
          drawing's chips, the chargée's ticks by their doors, the observed one disabled with its wait, the
          advance guarded in the drawing's French, « Éditer le bon de retrait » greyed with T-2; the bank's
          five as information beside them (`lending.ConditionLifted`'s consumer). */}
      {dossier.stage === "conditions" ? (
        <ConditionsPanel
          conditions={props.conditions ?? null}
          bankConditions={props.bankConditions ?? null}
          settlement={props.settlement ?? null}
          busy={props.busy}
          onLift={props.onLiftCondition}
          onTransmit={() => {
            props.onAdvance(ACTION.allConditionsLifted);
          }}
        />
      ) : null}
      {/* `T-2` (WF-CON-18 steps 5 and 6, `P2-DOS-003`): the règlement card with one row per leg, the
          console's copy of the truth the client and the dealer see; the bon's state beneath it. */}
      {(dossier.stage === "conditions" || dossier.stage === "decaisse") &&
      props.settlement !== null &&
      props.settlement !== undefined ? (
        <SettlementCard settlement={props.settlement} />
      ) : null}
      {dossier.stage !== "intake" &&
      dossier.stage !== "expertise" &&
      dossier.stage !== "collecte" &&
      dossier.stage !== "analyse" &&
      dossier.stage !== "conditions"
        ? props.actions
            .filter((action) => !IN_PLACE_ACTIONS.includes(action))
            // `L-2`, `M3`: the three the panels above draw themselves.
            .filter(
              (action) =>
                action !== ACTION.recordOffer &&
                action !== ACTION.clientRetainedOffer &&
                action !== ACTION.launchInsurancePlacement &&
                // `T-1`, `M2`: the « Offre » panel draws the move to the conditions itself.
                action !== ACTION.proceedToConditions &&
                // `L-3b`, `M2`: the « Soumis » panel draws these two itself.
                action !== ACTION.recordRefusal &&
                action !== ACTION.allBanksRefused &&
                // `AA-4b`: at Prêt the chargée chooses the banks herself (the panel below); this move is the submission door's.
                action !== ACTION.confirmSubmission,
            )
            .map((action) => (
              <button
                key={action}
                type="button"
                data-testid={`dossier-action-${action.slice(0, 12).replaceAll(/\W+/gu, "-")}`}
                disabled={props.busy}
                onClick={() => {
                  props.onAdvance(action);
                }}
                className="w-fit rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {action === ACTION.sendToAnalysis ? t("console.dossier.envoyerAnalyse") : action}
              </button>
            ))
        : null}

      {/* `A-RECOLLECTE`: sends a file back to the collecte , from analyse and pret only, to the three roles who may act. */}
      {/* The send's refusal, as the console says it (the coverage gate's sentence, or `P0-DOS-074`'s withdrawn computation): under « Soumettre aux banques », form or no form. */}
      {dossier.stage === "pret" && props.sendRefusal !== undefined && props.sendRefusal !== null ? (
        <div
          role="alert"
          data-testid="dossier-envoi-refuse"
          data-tone="warn"
          className="rounded-xl border border-amber-100 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700"
        >
          {props.sendRefusal}
        </div>
      ) : null}
      {dossier.stage === "pret" && props.onReturnToCollecte !== undefined ? (
        props.open === "remettre" ? (
          <ReturnToCollecteForm {...props} />
        ) : (
          <button
            type="button"
            data-testid="dossier-remettre"
            disabled={props.busy}
            onClick={() => {
              props.setOpen("remettre");
            }}
            className="w-fit rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {t("console.dossier.remettre")}
          </button>
        )
      ) : null}

      {/* `WF-CON-19` and `P1-REST`: the closure, on every open stage , and `decaisse` is CLOSED (02 §2.4.1's
          terminal row, `ETAPES_CLOSES`): no control on a décaissé file (`T-2`, found at the shooting). It is an EXIT,
          not one of the stage's decisions: a zone of its own at the foot of the panel, a quiet text control, and the
          sentence that tells it from « Refuser ». */}
      {dossier.stage === "decaisse" ? null : (
        <div data-testid="dossier-sortie" className="mt-2 border-t border-gray-100 pt-3">
          <p className="text-xs font-medium text-gray-500">{t("console.dossier.sortieTitre")}</p>
          <p className="mt-0.5 text-sm text-gray-500">{t("console.dossier.sortieMots")}</p>
          {props.open === "clore" ? (
            <div className="mt-2">
              <CloseForm {...props} />
            </div>
          ) : (
            <button
              type="button"
              data-testid="dossier-clore"
              disabled={props.busy}
              onClick={() => {
                props.setOpen("clore");
              }}
              className="mt-1 w-fit text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-40"
            >
              {t("console.dossier.clore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * `AA-3a2` (D-1018-1, `DX-010`): **the Collecte screen , human work only, ONE primary action.**
 *
 * What stands here is what a person does: the pièces still to receive (derived, never typed), the fee's state, the
 * employer verification and the chases (their own cards, below). Nothing of the machine's reading appears , that is
 * the Analyse screen's. The one primary action is « Envoyer en analyse » (the wire action keeps its string), greyed
 * with the count of missing pièces as its visible reason; « Clore sans suite » below it is secondary.
 */
function CollectePanel(props: PanelProps): React.JSX.Element {
  const { dossier } = props;
  const sendOffered = props.actions.includes(ACTION.sendToAnalysis);
  // `G3-3` (the founder's decision): every pièce is in and the buyer's own send has not been made. The sentence is the
  // api's chip, said once more where the chargée works: the same fact (`client_sent_at`, false until his send).
  const awaitingSend = props.manquants === 0 && (dossier.clientSentAt ?? null) === null;
  return (
    <div data-testid="dossier-collecte" className="flex flex-col gap-3">
      {/* The drawing's « Ce qui reste à faire », DERIVED , one line per missing pièce, and the fee's own state. */}
      <div data-testid="dossier-reste">
        <p className="text-sm font-medium text-gray-700">{t("console.dossier.resteAFaire")}</p>
        <ul className="mt-1.5 space-y-1">
          {props.pieces
            .filter((piece) => piece.status !== "recue" && piece.status !== "sansObjet")
            .map((piece) => (
              <li key={piece.code} className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                {t("console.dossier.recevoir")} {piece.label}
              </li>
            ))}
          <li className="flex items-center gap-2 text-sm text-gray-500">
            {props.fee?.clientSettledOn ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            ) : (
              <Clock className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
            )}
            {props.fee?.clientSettledOn
              ? `${t("console.dossier.fraisRegles")} (${formatMoney(props.fee.clientAmount)})`
              : t("console.dossier.fraisAttendus")}
          </li>
        </ul>
      </div>
      {awaitingSend ? (
        <p data-testid="collecte-attente-envoi" className="text-sm font-medium text-gray-700">
          {dossier.attente}
        </p>
      ) : null}
      {sendOffered ? (
        props.manquants > 0 ? (
          <div className="flex flex-col gap-2">
            <GuardedButton
              disabled
              reason={missingWords(props.manquants)}
              variant="primary"
              testId="dossier-envoyer-analyse"
            >
              {t("console.dossier.envoyerEnAnalyse")}
            </GuardedButton>
            {/* The drawing's own control here as well as on the pièces card: the same form, opened from the panel. */}
            <button
              type="button"
              data-testid="dossier-televerser-panneau"
              onClick={() => {
                props.setOpen(props.open === "televerser" ? null : "televerser");
              }}
              className="w-fit rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("console.dossier.televerser")}
            </button>
          </div>
        ) : (
          <GuardedButton
            variant="primary"
            testId="dossier-envoyer-analyse"
            onClick={() => {
              if (!props.busy) props.onAdvance(ACTION.sendToAnalysis);
            }}
          >
            {t("console.dossier.envoyerEnAnalyse")}
          </GuardedButton>
        )
      ) : null}
    </div>
  );
}

/**
 * `AA-3a2` (D-1018-1, D-1018-AA2, `DX-010`): **the Analyse screen , the results, then the chargée decides.**
 *
 * The results are the filter's last computation, in words (the reading, the analysis and the synthesis stand in their
 * own cards beneath). Then three decisions, and nothing moves without her: (1) « Valider et passer à Prêt », the ONE
 * primary action , never automatic, greyed with the door's own sentence until the filter has been computed with an
 * eligible bank; she picks the banks at Prêt; (2) « Renvoyer en Collecte », with a reason, the existing door; (3)
 * « Refuser », with a reason typed by her. The filter's own button is folded below the decisions, unchanged.
 */
function AnalysePanel(props: PanelProps): React.JSX.Element {
  const computation = props.filterComputation ?? null;
  return (
    <div data-testid="dossier-analyse" className="flex flex-col gap-3">
      {/* The result of the filter, in words: the kept computation, or the verdict that failed, or none yet. */}
      <div data-testid="dossier-analyse-resultats">
        {(props.filterFailure ?? null) !== null ? (
          <p
            className="rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm leading-relaxed text-rose-700"
            data-testid="dossier-filtre-verdict"
          >
            {fill("console.dossier.filtreEchoue", { motif: props.filterFailure ?? "" })}
          </p>
        ) : null}
        <p className="text-sm text-gray-700" data-testid="dossier-filtre-calcul">
          {computation === null
            ? t("console.dossier.validerNonCalcule")
            : fill("console.dossier.filtreCalcule", {
                eligibles:
                  computation.eligibleCount === 1
                    ? t("console.dossier.banqueEligibleUne")
                    : fill("console.dossier.banquesEligibles", { n: String(computation.eligibleCount) }),
              })}
        </p>
      </div>

      {/* `AA-4a`: « Lancer le filtre Hakili » leaves the console. The one button « Lancer l'analyse Hakili » chains the filter and
          the banks after the underwriting; the advance branch (`stages.advance`, « Lancer le filtre Hakili ») stays an API door. */}
    </div>
  );
}

/**
 * `P2` (the founder's walk, the architect's note): **her three decisions, closing the Analyse card.** Drawn by the card once its results exist
 * (never before): « Valider et passer à Prêt » (the one primary), « Renvoyer en Collecte », and « Refuser le dossier », a rose outline
 * apart from the other two, with the sentence that says what it does. « Clore sans suite » is not a decision: it stands in the exit zone
 * (`P1-REST`).
 */
function AnalyseDecisions(props: PanelProps & { readonly without?: boolean }): React.JSX.Element {
  const computation = props.filterComputation ?? null;
  // `READING-REQUIRED`, C9: nothing is live while the read it depends on (the reading) has failed; the failure's own words are the reason.
  const readingReadFailure = props.partRefusals?.["lecture"] ?? null;
  // Without the analysis (it failed, or it cannot run) she decides anyway: « Valider » has nothing to validate and says so.
  const validateReason =
    readingReadFailure ??
    (props.without
      ? t("console.dossier.validerSansAnalyse")
      : computation === null
        ? t("console.dossier.validerNonCalcule")
        : computation.eligibleCount === 0
          ? t("console.dossier.validerAucuneBanque")
          : null);
  return (
    <div data-testid="dossier-decisions" className="flex flex-col gap-2">
      <p className="text-sm font-medium text-gray-700">{t("console.dossier.decisionsTitre")}</p>
      {props.without ? (
        <p className="text-sm text-gray-500" data-testid="dossier-decisions-sans-analyse">
          {t("console.analyse.sansAnalyse")}
        </p>
      ) : (
        <p className="text-sm text-gray-500">{t("console.dossier.decisionsMots")}</p>
      )}
      {validateReason !== null ? (
        <GuardedButton disabled reason={validateReason} variant="primary" testId="dossier-valider">
          {t("console.dossier.valider")}
        </GuardedButton>
      ) : (
        <GuardedButton
          variant="primary"
          testId="dossier-valider"
          onClick={() => {
            if (!props.busy) props.onAdvance(ACTION.validateToPret);
          }}
        >
          {t("console.dossier.valider")}
        </GuardedButton>
      )}
      {/* `READING-REQUIRED`: the failed-read pattern for the reading's own read (amber notice, « Réessayer » inside) ... */}
      {readingReadFailure !== null && props.onRetryRead !== undefined ? (
        <div
          data-testid="dossier-lecture-echec"
          className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-100 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700"
        >
          <span>{readingReadFailure}</span>
          <GuardedButton
            variant="secondary"
            testId="dossier-lecture-reessayer"
            onClick={() => {
              props.onRetryRead?.();
            }}
          >
            {t("common.action.reessayer")}
          </GuardedButton>
        </div>
      ) : null}
      {/* ... and the door's refusal for a reading never launched, as served: a refusal, not a failed read, so no « Réessayer ». */}
      {props.readingRefusal === undefined || props.readingRefusal === null ? null : (
        <div
          role="alert"
          data-testid="dossier-lecture-non-lancee"
          data-tone="warn"
          className="rounded-xl border border-amber-100 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700"
        >
          {props.readingRefusal}
        </div>
      )}
      {props.onReturnToCollecte === undefined ? null : props.open === "remettre" ? (
        <ReturnToCollecteForm {...props} />
      ) : (
        <button
          type="button"
          data-testid="dossier-remettre"
          disabled={props.busy}
          onClick={() => {
            props.setOpen("remettre");
          }}
          className="w-fit rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {t("console.dossier.remettre")}
        </button>
      )}
      {props.onRefuse === undefined ? null : props.open === "refuser" ? (
        <RefuseForm {...props} />
      ) : (
        <div className="flex flex-col gap-1">
          {/* The rose already in the closed palette for a refusal (`border-rose-200`, `text-rose-700`, `bg-rose-50`: the « Refusé » filter
                chip of the list), as an outline, apart from « Renvoyer en Collecte ». */}
          <button
            type="button"
            data-testid="dossier-refuser"
            disabled={props.busy}
            onClick={() => {
              props.setOpen("refuser");
            }}
            className="w-fit rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-40"
          >
            {t("console.dossier.refuser")}
          </button>
          <p className="text-sm text-gray-500" data-testid="dossier-refuser-consequence">
            {t("console.dossier.refuserConsequence")}
          </p>
        </div>
      )}
    </div>
  );
}

/** `AA-3a2` (D-1018-1): « Refuser le dossier » , a reason typed by the chargée, required, 300 characters at most. */
function RefuseForm(props: PanelProps): React.JSX.Element {
  const [reason, setReason] = useState("");
  const empty = reason.trim() === "";
  return (
    <div
      data-testid="dossier-refuser-form"
      className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
    >
      <p className="mb-2 text-sm text-gray-500">{t("console.dossier.refuserMots")}</p>
      <label className="mb-1 block text-sm text-gray-500" htmlFor="dossier-refuser-reason">
        {t("console.dossier.refuserMotif")}
      </label>
      <input
        id="dossier-refuser-reason"
        data-testid="dossier-refuser-reason"
        value={reason}
        maxLength={300}
        onChange={(event) => {
          setReason(event.target.value);
        }}
        className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-200"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {empty ? (
          <GuardedButton
            disabled
            reason={t("console.dossier.refuserMotifRequis")}
            variant="secondary"
            testId="dossier-refuser-confirmer"
          >
            {t("console.dossier.refuserConfirmer")}
          </GuardedButton>
        ) : (
          <GuardedButton
            variant="secondary"
            testId="dossier-refuser-confirmer"
            onClick={() => {
              if (props.busy) return;
              props.onRefuse?.(reason.trim());
              props.setOpen(null);
            }}
          >
            {t("console.dossier.refuserConfirmer")}
          </GuardedButton>
        )}
        <button
          type="button"
          onClick={() => {
            props.setOpen(null);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700"
        >
          {t("console.dossier.fermer")}
        </button>
      </div>
    </div>
  );
}

/**
 * `intake`, in the AMENDED order: Hakili WAITS. The fee's state in words from
 * `getFeeStates`, and no advance , the buyer launches from his own screen.
 */
function IntakePanel({
  fee,
  vue,
  onFlag,
  onOpenSequestre,
}: {
  readonly fee: FeeView | null;
  readonly vue: Vue;
  readonly onFlag?: (() => Promise<boolean>) | undefined;
  readonly onOpenSequestre?: (() => void) | undefined;
}): React.JSX.Element {
  const settled = fee !== null && (fee.clientStatus === "paye" || fee.clientSettledOn !== null);
  const words =
    fee === null
      ? t("console.dossier.intakeAucuneDemande")
      : settled
        ? `${t("console.dossier.intakeRecus")} ${day(fee.clientSettledOn)} (${formatMoney(fee.clientAmount)}).`
        : t("console.dossier.intakeAttenteVirement");
  const [flagged, setFlagged] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * `FX-3`, `M2 (d)` (`D6`, and its addendum of 29 September): **operations and finance, on one panel,
   * as two different gestures.**
   *
   * A transfer is awaited and the file is a chargé's: the state is already in words above, and what was
   * missing is anything to DO about it , the sitting's own finding, `console.dossier.intakeHakiliAttend`
   * telling a chargé that « rien ne se fait avancer depuis cette console ».
   *
   * The two views of row 28 (`confirmerVirementFrais`: administration, responsable dossiers) get a way to
   * the séquestre, where the confirmation already lives, and **no confirm control is drawn here**: the
   * ruling's own words are that nothing is drawn twice, and the panel that reads these transfers is the one
   * both of those views already hold. Everybody else gets « Signaler au contrôle », which pings them.
   */
  const mayConfirm = vue === "admin" || vue === "resp";
  return (
    <div data-testid="dossier-intake" className="rounded-xl bg-gray-50 px-3.5 py-3">
      <p className="text-sm font-medium text-gray-900">{words}</p>
      <p className="mt-1 text-sm text-gray-500">{t("console.dossier.intakeHakiliAttend")}</p>
      {settled ? null : mayConfirm ? (
        onOpenSequestre === undefined ? null : (
          <button
            type="button"
            data-testid="intake-sequestre"
            onClick={onOpenSequestre}
            className="mt-2.5 w-fit rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("console.dossier.intakeVoirSequestre")}
          </button>
        )
      ) : onFlag === undefined ? null : flagged ? (
        <p data-testid="intake-signale" className="mt-2.5 text-sm font-medium text-emerald-700">
          {t("console.dossier.intakeSignale")}
        </p>
      ) : (
        <>
          <button
            type="button"
            data-testid="intake-signaler"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onFlag()
                .then((done) => {
                  setFlagged(done);
                })
                .finally(() => {
                  setBusy(false);
                });
            }}
            className="mt-2.5 w-fit rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {t("console.dossier.intakeSignalerControle")}
          </button>
          <p className="mt-1.5 text-sm text-gray-500">{t("console.dossier.intakeSignalerAide")}</p>
        </>
      )}
    </div>
  );
}

/** `WF-CON-09` and `10`: schedule, the fallback report, and the three outcomes. */
function ExpertisePanel(props: PanelProps): React.JSX.Element {
  const verdictWord =
    props.verdict === null
      ? null
      : props.verdict === "conforme"
        ? t("console.dossier.verdictConforme")
        : props.verdict === "ecartprix"
          ? t("console.dossier.verdictEcartPrix")
          : t("console.dossier.verdictNonConforme");
  // The drawing's own fork: BEFORE a report, the schedule and the fallback
  // entry; AFTER one, the verdict and its outcomes, and the two controls gone.
  if (verdictWord === null) {
    // `round CN-H`, `M0(b)`: the report is the RESPONSABLE's , `06` §6.1.5
    // row 18 `saisirRapportExpertise` (admin, responsable; a chargé « non »,
    // WF-CON-34: « A chargé de dossier cannot enter or correct a report »).
    // From a chargé's chair the two controls are greyed with that reason in
    // their title, and the screen never asks the queue or the grid for him.
    const mayReport = props.vue === "admin" || props.vue === "resp";
    return (
      <>
        <div className="flex flex-wrap items-center gap-2">
          {mayReport ? (
            <button
              type="button"
              data-testid="dossier-programmer"
              onClick={props.onGoExpertises}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("console.dossier.programmerExpertise")}
            </button>
          ) : null}
          {!mayReport ? null : props.demandId === null ? (
            <GuardedButton
              disabled
              reason={t("console.dossier.saisirRapportSansDemande")}
              variant="secondary"
            >
              {t("console.dossier.saisirRapport")}
            </GuardedButton>
          ) : (
            <button
              type="button"
              data-testid="dossier-saisir-rapport"
              disabled={props.busy}
              onClick={() => {
                props.setOpen(props.open === "rapport" ? null : "rapport");
              }}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              {t("console.dossier.saisirRapport")}
            </button>
          )}
        </div>

        <p className="text-sm leading-relaxed text-gray-500">{t("console.dossier.attenteRapport")}</p>

        {props.open === "rapport" && props.grid !== null ? (
          <ReportForm grid={props.grid} busy={props.busy} onFile={props.onFileReport} />
        ) : null}
        <div data-testid="dossier-verdict" className="rounded-xl bg-gray-50 px-3.5 py-3">
          <p className="text-xs font-medium text-gray-700">{t("console.dossier.verdictTitre")}</p>
          <p className="mt-0.5 text-sm text-gray-900">{t("console.dossier.verdictAucun")}</p>
        </div>
      </>
    );
  }
  return (
    <>
      {/* The verdict READ-ONLY, the grid's word and never a free choice. */}
      <div data-testid="dossier-verdict" className="rounded-xl bg-gray-50 px-3.5 py-3">
        <p className="text-xs font-medium text-gray-700">{t("console.dossier.verdictTitre")}</p>
        <p className="mt-0.5 text-sm text-gray-900">{verdictWord}</p>
        {props.report === undefined || props.report === null ? null : (
          <p className="mt-1 text-xs text-gray-500" data-testid="dossier-rapport-provenance">
            {provenanceWords(props.report)}
          </p>
        )}
        {props.report?.documentId === undefined || props.report.documentId === null ? null : (
          <p className="mt-1 text-xs text-indigo-700" data-testid="dossier-rapport-piece">
            {t("console.dossier.rapportPiece")}
          </p>
        )}
        {/* Round `C6` (`OD-33`): the fingerprint printed wherever the document is
            opened, so an alteration cannot pass unseen; the version beside it. */}
        {props.report?.documentSha256 === undefined || props.report.documentSha256 === null ? null : (
          <p className="mt-1 text-xs text-gray-500 tabular-nums" data-testid="dossier-rapport-empreinte">
            {fill("console.dossier.rapportEmpreinte", {
              version: String(props.report.version ?? 1),
              sha: props.report.documentSha256,
            })}
          </p>
        )}
        {props.verdict === "ecartprix" ? (
          <p className="mt-1 text-sm text-amber-700">{t("console.dossier.ecartPrixNote")}</p>
        ) : null}
      </div>

      {/* The outcomes: only the ones the door lists AND the verdict permits. A
          « conforme » advance on a non-conforming report is refused by the door
          in its words; drawing it would offer a lie. */}
      {props.verdict === "conforme" && props.actions.includes(ACTION.reportConforming) ? (
        <button
          type="button"
          data-testid="dossier-conforme"
          disabled={props.busy}
          onClick={() => {
            props.onAdvance(ACTION.reportConforming);
          }}
          className="w-fit rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
        >
          {ACTION.reportConforming}
        </button>
      ) : null}
      {props.verdict === "nonconforme" && props.actions.includes(ACTION.reportNonConforming) ? (
        <button
          type="button"
          data-testid="dossier-non-conforme"
          disabled={props.busy}
          onClick={() => {
            props.onAdvance(ACTION.reportNonConforming);
          }}
          className="w-fit rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
        >
          {ACTION.reportNonConforming}
        </button>
      ) : null}
    </>
  );
}

function CloseForm(props: PanelProps): React.JSX.Element {
  const [reason, setReason] = useState("");
  // `K-1`, `M4`: the coded motive beside the mandatory free reason (WF-CON-19 amended).
  const [code, setCode] = useState("");
  return (
    <div
      data-testid="dossier-clore-form"
      className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
    >
      <label className="mb-1 block text-xs text-gray-500" htmlFor="dossier-clore-code">
        {t("console.dossier.cloreCode")}
      </label>
      <select
        id="dossier-clore-code"
        data-testid="dossier-clore-code"
        value={code}
        onChange={(event) => {
          setCode(event.target.value);
        }}
        className="mb-2 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-indigo-200"
      >
        <option value="">{t("console.dossier.cloreCodeChoisir")}</option>
        {CLOSURE_CODE_WORDS.map((one) => (
          <option key={one.code} value={one.code}>
            {t(one.key)}
          </option>
        ))}
      </select>
      <label className="mb-1 block text-xs text-gray-500" htmlFor="dossier-clore-reason">
        {t("console.dossier.cloreMotif")}
      </label>
      <input
        id="dossier-clore-reason"
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
        }}
        className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-200"
      />
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          data-testid="dossier-clore-confirmer"
          disabled={props.busy || reason.trim() === "" || code === ""}
          onClick={() => {
            props.onClose(reason.trim(), code);
          }}
          className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-40"
        >
          {t("console.dossier.cloreConfirmer")}
        </button>
        <button
          type="button"
          onClick={() => {
            props.setOpen(null);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
        >
          {t("console.dossier.fermer")}
        </button>
      </div>
    </div>
  );
}

function ReturnToCollecteForm(props: PanelProps): React.JSX.Element {
  // `AA-3d2` (D-1018-AA2): the dialog OPENS on the items editor , what the client must complete, in her own words, each
  // line tied to one of HIS pièces or free. Then her message, then the internal motive (never shown to him).
  const [items, setItems] = useState<
    readonly { readonly key: number; readonly pieceCode: string; readonly text: string }[]
  >(() =>
    (props.returnPrefill ?? []).map((line, key) => ({ key, pieceCode: line.pieceCode, text: line.text })),
  );
  const [counter, setCounter] = useState((props.returnPrefill ?? []).length);
  // `COVERAGE-READ-CHECK`: the form opened by itself (the door refused the send), so the focus goes to its first field.
  const formRef = useRef<HTMLDivElement>(null);
  const openedByItself = (props.returnPrefill ?? []).length > 0;
  useEffect(() => {
    if (openedByItself) formRef.current?.querySelector<HTMLElement>("select, input, textarea")?.focus();
  }, [openedByItself]);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  // The pièces the BUYER owes (never the seller's), still a pièce of the file.
  const owed = props.pieces.filter((one) => one.suppliedBy !== "vendeur" && one.status !== "sansObjet");
  const add = (pieceCode: string, text: string): void => {
    setItems((now) => [...now, { key: counter, pieceCode, text }]);
    setCounter((now) => now + 1);
  };
  const change = (key: number, over: { readonly pieceCode?: string; readonly text?: string }): void => {
    setItems((now) => now.map((one) => (one.key === key ? { ...one, ...over } : one)));
  };
  const waiting = items.some((one) => one.text.trim() === "")
    ? t("console.dossier.remettreElementVide")
    : reason.trim() === ""
      ? t("console.dossier.remettreMotifInterneRequis")
      : null;
  const submit = (): void => {
    if (props.busy) return;
    const cleaned = items.map((one) => ({
      pieceCode: one.pieceCode === "" ? null : one.pieceCode,
      text: one.text.trim(),
    }));
    const message = note.trim() === "" ? null : note.trim();
    // With neither items nor a message the call is today's, exactly.
    if (cleaned.length === 0 && message === null) props.onReturnToCollecte?.(reason.trim());
    else props.onReturnToCollecte?.(reason.trim(), { items: cleaned, note: message });
    props.setOpen(null);
  };
  return (
    <div
      ref={formRef}
      data-testid="dossier-remettre-form"
      className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
    >
      <div data-testid="dossier-remettre-items">
        <p className="text-sm font-medium text-gray-700">{t("console.dossier.remettreItemsTitre")}</p>
        <p className="mt-0.5 text-sm text-gray-500">{t("console.dossier.remettreItemsMots")}</p>
        <ul className="mt-2 flex flex-col gap-2">
          {items.map((item, index) => (
            <li
              key={item.key}
              data-testid={`dossier-remettre-item-${String(index)}`}
              className="flex flex-col gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-2"
            >
              <label
                className="text-sm text-gray-500"
                htmlFor={`dossier-remettre-item-piece-${String(item.key)}`}
              >
                {t("console.dossier.remettreElementPiece")}
              </label>
              <select
                id={`dossier-remettre-item-piece-${String(item.key)}`}
                data-testid={`dossier-remettre-item-${String(index)}-piece`}
                value={item.pieceCode}
                onChange={(event) => {
                  change(item.key, { pieceCode: event.target.value });
                }}
                className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
              >
                <option value="">{t("console.dossier.remettrePieceLibre")}</option>
                {owed.map((one) => (
                  <option key={one.code} value={one.code}>
                    {one.label}
                  </option>
                ))}
              </select>
              <label
                className="text-sm text-gray-500"
                htmlFor={`dossier-remettre-item-texte-${String(item.key)}`}
              >
                {t("console.dossier.remettreElementTexte")}
              </label>
              <input
                id={`dossier-remettre-item-texte-${String(item.key)}`}
                data-testid={`dossier-remettre-item-${String(index)}-texte`}
                value={item.text}
                maxLength={200}
                onChange={(event) => {
                  change(item.key, { text: event.target.value });
                }}
                className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-200"
              />
              <button
                type="button"
                data-testid={`dossier-remettre-item-${String(index)}-retirer`}
                onClick={() => {
                  setItems((now) => now.filter((one) => one.key !== item.key));
                }}
                className="w-fit rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700"
              >
                {t("console.dossier.remettreRetirer")}
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="dossier-remettre-ajouter"
            disabled={items.length >= 10}
            onClick={() => {
              add("", "");
            }}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-40"
          >
            {t("console.dossier.remettreAjouter")}
          </button>
          {owed.length === 0 ? null : (
            <select
              data-testid="dossier-remettre-ajouter-piece"
              aria-label={t("console.dossier.remettreAjouterPiece")}
              value=""
              disabled={items.length >= 10}
              onChange={(event) => {
                const chosen = owed.find((one) => one.code === event.target.value);
                if (chosen !== undefined) add(chosen.code, chosen.label);
              }}
              className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
            >
              <option value="">{t("console.dossier.remettreAjouterPiece")}</option>
              {owed.map((one) => (
                <option key={one.code} value={one.code}>
                  {one.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm text-gray-500" htmlFor="dossier-remettre-note">
          {t("console.dossier.remettreNote")}
        </label>
        <textarea
          id="dossier-remettre-note"
          data-testid="dossier-remettre-note"
          value={note}
          maxLength={500}
          rows={3}
          onChange={(event) => {
            setNote(event.target.value);
          }}
          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-200"
        />
      </div>
      <div>
        <p className="mb-2 text-sm text-gray-500">{t("console.dossier.remettreMots")}</p>
        <label className="mb-1 block text-sm text-gray-500" htmlFor="dossier-remettre-reason">
          {t("console.dossier.remettreMotif")}
        </label>
        <input
          id="dossier-remettre-reason"
          data-testid="dossier-remettre-reason"
          value={reason}
          maxLength={300}
          onChange={(event) => {
            setReason(event.target.value);
          }}
          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-200"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {waiting === null ? (
          <GuardedButton variant="secondary" testId="dossier-remettre-confirmer" onClick={submit}>
            {t("console.dossier.remettreConfirmer")}
          </GuardedButton>
        ) : (
          <GuardedButton disabled reason={waiting} variant="secondary" testId="dossier-remettre-confirmer">
            {t("console.dossier.remettreConfirmer")}
          </GuardedButton>
        )}
        <button
          type="button"
          onClick={() => {
            props.setOpen(null);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700"
        >
          {t("console.dossier.fermer")}
        </button>
      </div>
    </div>
  );
}

function ReassignForm(props: PanelProps | DossierProps): React.JSX.Element {
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");
  return (
    <div data-testid="dossier-reattribuer-form" className="flex flex-col gap-2">
      <label className="text-xs text-gray-500" htmlFor="dossier-reattribuer-a">
        {t("console.dossier.reattribuerA")}
      </label>
      <select
        id="dossier-reattribuer-a"
        value={userId}
        onChange={(event) => {
          setUserId(event.target.value);
        }}
        className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs outline-none"
      >
        <option value="">{DASH}</option>
        {props.assignable.map((one) => (
          <option key={one.id} value={one.id}>
            {one.fullName}
          </option>
        ))}
      </select>
      <label className="text-xs text-gray-500" htmlFor="dossier-reattribuer-reason">
        {t("console.dossier.reattribuerMotif")}
      </label>
      <input
        id="dossier-reattribuer-reason"
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
        }}
        className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs outline-none"
      />
      <button
        type="button"
        data-testid="dossier-reattribuer-confirmer"
        disabled={props.busy || userId === "" || reason.trim() === ""}
        onClick={() => {
          props.onReassign(userId, reason.trim());
        }}
        className="w-fit rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
      >
        {t("console.dossier.reattribuerConfirmer")}
      </button>
    </div>
  );
}

// ------------------------------------------------------------- the pieces

/** `PieceSource`, in words: where a received pièce came from (WF-CON-07's provenance). */
const SOURCES = new Map<string, string>([
  ["client", "console.dossier.sourceClient"],
  ["lien", "console.dossier.sourceLien"],
  ["whatsapp", "console.dossier.sourceWhatsapp"],
  ["agence", "console.dossier.sourceAgence"],
  ["courriel", "console.dossier.sourceCourriel"],
]);

/** `C7.6`: the seller's pièce, by the channel the office received it. */
const SELLER_CHANNELS = new Map<string, string>([
  ["whatsapp", "console.dossier.sourceVendeurWhatsapp"],
  ["agence", "console.dossier.sourceVendeurAgence"],
  ["courriel", "console.dossier.sourceVendeurCourriel"],
  // `SELLER-DEPOSIT-LINK`: the dealer deposited it himself by the office's no-login link.
  ["lien", "console.dossier.sourceVendeurLien"],
]);

const provenance = (one: PieceView): string => {
  const source =
    one.provenance === "vehicule"
      ? "console.dossier.sourceFicheVehicule"
      : one.provenance === "concessionnaire"
        ? "console.dossier.sourceConcessionnaire"
        : one.provenance === "emise"
          ? "console.dossier.sourceEmise"
          : one.source === "vendeur"
            ? (SELLER_CHANNELS.get(one.receptionChannel ?? "") ?? "console.dossier.sourceVendeur")
            : one.source === null
              ? null
              : SOURCES.get(one.source);
  const words = source === undefined || source === null ? t("console.dossier.pieceRecue") : t(source);
  // `A-DLR`, Arc B (+D): the vehicle's papers name no person, and the dealership's own deposit names its author
  // in brackets , never « déposée par le concessionnaire par <nom> ».
  const head =
    one.provenance === "vehicule"
      ? words
      : one.provenance === "concessionnaire" || one.provenance === "emise"
        ? one.auteur === null
          ? words
          : `${words} (${one.auteur})`
        : [words, one.auteur === null ? "" : `${t("console.dossier.pieceProvenance")} ${one.auteur}`]
            .filter((part) => part !== "")
            .join(" ");
  const parts = [
    head,
    one.recuLe === null ? "" : `${t("console.dossier.pieceLe")} ${day(one.recuLe)}`,
  ].filter((part) => part !== "");
  return parts.join(" ");
};

/**
 * `round C7`, `C7.2` and `C7.3`: **the drawing's « Pièces » card, live.**
 *
 * Every row says its provenance in words when received, its reason when
 * rejected, and « lien de dépôt envoyé » once a link went out. The row's
 * control is the DOOR's word (`action`), the chase mints a link answered
 * once and shown as the tour sheet's is (`C5.0.0(b)`): hidden by default,
 * copied without being shown. « Tout relancer avec les liens » from two
 * missing upward, one link per pièce, one grouped journal line.
 */
/** `S4-9` widened by `G3-2`: the list is `@hakili/domain-types`' own, the door's list; the door refuses every other code. */
const CONSULTABLE_DOCUMENTS: readonly string[] = CONSULTABLE_DOCUMENT_CODES;

function DocumentsSection(props: PanelProps): React.JSX.Element {
  // `VISITE-SANS-OBJET`: a « sans objet » pièce is neither received nor missing , it is satisfied, and reads so.
  const missing = props.pieces.filter((one) => one.status !== "recue" && one.status !== "sansObjet");
  const received = props.pieces.length - missing.length;
  // `CONSOLE-MONTHS-QUESTION`, the next gap: a multi-file pièce is complete when its files' months reach the count it asks for. One that holds fewer is RECEIVED (the stored status says so) but not
  // done, so it stays offered for another file, with its months question. A pièce whose months the door does not serve is read as done, as before.
  const shortOfMonths = (one: PieceView): boolean =>
    one.status === "recue" &&
    one.multiFile === true &&
    one.expectedFiles !== null &&
    one.expectedFiles !== undefined &&
    one.coveredMonths !== null &&
    one.coveredMonths !== undefined &&
    one.coveredMonths < one.expectedFiles;
  const offered = props.pieces.filter((one) => missing.includes(one) || shortOfMonths(one));
  // `C7.6`: the grouped chase counts the BUYER's missing pièces , the rows
  // the door offers a chase on; a link never asks a buyer for a seller's paper.
  const chaseable = missing.filter((one) => one.action !== null && one.action !== undefined);
  const [documentCode, setDocumentCode] = useState("");
  const [channel, setChannel] = useState("");
  const [note, setNote] = useState("");
  // `round S4-14`: the files chosen for this filing , the door no longer flips a pièce to
  // « reçue » without a real document behind it. `fileInputKey` remounts the native input
  // after a submit, which is the only way to clear a `type="file"` element's own value.
  const [files, setFiles] = useState<readonly File[]>([]);
  // `CONSOLE-MONTHS-QUESTION` (`PIECES-COUNT`, A1): the months each chosen file holds, by file, « 1 mois » until she says otherwise (the door's own default).
  const [months, setMonths] = useState<readonly number[]>([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  // `VISITE-SANS-OBJET`: the visite's mark or undo being asked, and its reason.
  const [marking, setMarking] = useState<{ readonly code: string; readonly undo: boolean } | null>(null);
  const [markReason, setMarkReason] = useState("");
  const [identityOpen, setIdentityOpen] = useState(false);
  const [gender, setGender] = useState<"f" | "m" | "">("");
  const [holderName, setHolderName] = useState("");
  const [consulting, setConsulting] = useState<string | null>(null);
  const chosen = documentCode === "" ? (offered[0]?.code ?? "") : documentCode;
  // `AA-3a2` (D-1018-6): every confirm below that can be greyed gives its visible reason (`GuardedButton`).
  const uploadWaiting =
    channel === ""
      ? t("console.dossier.televerserCanalRequis")
      : files.length === 0
        ? t("console.dossier.televerserFichierRequis")
        : chosen === ""
          ? t("console.dossier.televerserCanalRequis")
          : null;
  const markWaiting = markReason.trim() === "" ? t("console.dossier.motifRequis") : null;
  const rejectWaiting = reason.trim() === "" ? t("console.dossier.motifRequis") : null;
  const identityWaiting =
    gender === ""
      ? t("console.dossier.civiliteRequise")
      : holderName.trim() === ""
        ? t("console.dossier.titulaireRequis")
        : null;
  // `S4-5-MULTI-FILE`: the picker admits the OS's own multi-select for a pièce that asks for
  // several documents, uploaded one after another through the SAME door; every other pièce stays single.
  const chosenMultiFile = offered.find((one) => one.code === chosen)?.multiFile ?? false;
  // The question is asked for a multi-file pièce whose count the door served; options run from one month to that count. Any other pièce is filed as before.
  const monthsAsked = chosenMultiFile
    ? (offered.find((one) => one.code === chosen)?.expectedFiles ?? null)
    : null;
  const atCollecte = props.canAct && props.dossier.stage === "collecte";
  // `verifierPieces`: row 31 of the matrix , the three Hakili roles that may
  // judge a pièce. The door enforces it; this decides whether to draw.
  const mayJudge = props.canAct;
  const identityRead = props.dossier.clientGender !== null && props.dossier.clientGender !== undefined;
  const identityDocument = props.pieces.find((one) => one.code === "pieceIdentite");
  const identityConsulted =
    identityDocument?.consultedAt !== null && identityDocument?.consultedAt !== undefined;
  /**
   * `S4-12`: **the same mechanism refuses a pièce at both its call sites.**
   * `atCollecte` alone left the control unreachable the moment a dossier
   * passed `collecte` , a failing filter had no way back to the client but
   * « Écarter au filtre Hakili », the dossier's own end. The door never
   * guarded by stage (`canReject` asks only the pièce's own status), so the
   * gap was this screen's alone: the SAME button, SAME reason form, SAME
   * `onReject`, now also offered while the last filter run failed.
   */
  const filterFailed = props.dossier.stage === "analyse" && (props.filterFailure ?? null) !== null;
  const mayReject = mayJudge && (atCollecte || filterFailed);
  // `VISITE-SANS-OBJET`: from the collecte to « prêt à soumettre », never at the banks (the door refuses it in French there).
  const mayMark =
    props.canAct &&
    ["collecte", "analyse", "pret"].includes(props.dossier.stage) &&
    props.dossier.stage !== "soumis";

  return (
    <section data-testid="dossier-pieces" className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">
          {t("console.dossier.piecesTitre")}
          {props.manquants > 0 ? (
            <span className="ml-2 text-sm font-medium text-amber-700">{missingWords(props.manquants)}</span>
          ) : null}
        </h2>
        {/* `M0(b)`: the pièces' own refusal, where the pièces are. */}
        {props.partRefusals?.["pieces"] === undefined ? null : (
          <p role="alert" data-testid="dossier-pieces-refus" className="text-sm text-rose-600">
            {props.partRefusals["pieces"]}
          </p>
        )}
        <div className="flex items-center gap-3">
          {/* A grouped chase makes sense from two pièces: for one, the row's own. */}
          {atCollecte && chaseable.length >= 2 ? (
            <button
              type="button"
              data-testid="dossier-relancer-tout"
              disabled={props.busy}
              onClick={props.onChaseAll}
              className="rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 px-3 py-1.5 text-sm font-medium disabled:opacity-40"
            >
              {t("console.dossier.relancerTout")}
            </button>
          ) : null}
          <span
            data-testid="dossier-pieces-compte"
            className={`text-sm font-medium ${props.manquants === 0 ? "text-emerald-700" : "text-amber-700"}`}
          >
            {String(received)} {t("console.dossier.sur")} {String(props.pieces.length)}{" "}
            {t("console.dossier.recues")}
          </span>
          {atCollecte ? (
            offered.length === 0 ? (
              <DoneLine testId="dossier-televerser-fait">
                {t("console.dossier.televerserToutesRecues")}
              </DoneLine>
            ) : (
              <button
                type="button"
                data-testid="dossier-televerser"
                onClick={() => {
                  props.setOpen(props.open === "televerser" ? null : "televerser");
                }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("console.dossier.televerser")}
              </button>
            )
          ) : null}
        </div>
      </div>

      {/* `WF-CON-07`: a pièce filed by hand is AUDITABLE , which pièce, by which
          channel, by whom. The channel has NO default, on purpose, and the door
          refuses without one; the control stays shut until it is chosen, with
          the reason beside it. */}
      {props.open === "televerser" && offered.length > 0 ? (
        <div
          data-testid="dossier-televerser-form"
          className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
        >
          <p className="mb-2 text-sm font-medium text-gray-700">{t("console.dossier.televerserTitre")}</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm text-gray-500">
              {t("console.dossier.televerserPiece")}
              <select
                value={chosen}
                onChange={(event) => {
                  setDocumentCode(event.target.value);
                  setFiles([]);
                  setMonths([]);
                  setFileInputKey((key) => key + 1);
                }}
                className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
              >
                {offered.map((one) => (
                  <option key={one.code} value={one.code}>
                    {one.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-500">
              {t("console.dossier.televerserFichier")}
              <input
                key={fileInputKey}
                type="file"
                data-testid="dossier-televerser-fichier"
                multiple={chosenMultiFile}
                onChange={(event) => {
                  const { files: picked } = event.target;
                  const chosenFiles = picked === null ? [] : Array.from(picked);
                  setFiles(chosenFiles);
                  setMonths(chosenFiles.map(() => 1));
                }}
                className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700"
              />
            </label>
            <label className="text-sm text-gray-500">
              {t("console.dossier.televerserCanal")}
              <select
                data-testid="dossier-televerser-channel"
                value={channel}
                onChange={(event) => {
                  setChannel(event.target.value);
                }}
                className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
              >
                <option value="">{DASH}</option>
                {[...CHANNELS.entries()].map(([code, key]) => (
                  <option key={code} value={code}>
                    {t(key)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-500">
              {t("console.dossier.televerserNote")}
              <input
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
                className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
              />
            </label>
          </div>
          {monthsAsked !== null && monthsAsked > 1 && files.length > 0 ? (
            <ul data-testid="dossier-televerser-mois" className="mt-2 flex flex-col gap-1.5">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${String(index)}`}
                  className="flex flex-wrap items-center gap-2 text-sm text-gray-500"
                >
                  <span className="min-w-0 truncate text-gray-700">{file.name}</span>
                  <label className="flex items-center gap-2">
                    {t("console.dossier.moisDuFichier")}
                    <select
                      data-testid={`dossier-televerser-mois-${String(index)}`}
                      value={String(months[index] ?? 1)}
                      onChange={(event) => {
                        const wanted = Number(event.target.value);
                        setMonths((now) => files.map((_, at) => (at === index ? wanted : (now[at] ?? 1))));
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm"
                    >
                      {Array.from({ length: monthsAsked }, (_, at) => at + 1).map((count) => (
                        <option key={count} value={String(count)}>
                          {monthsWords(count)}
                        </option>
                      ))}
                    </select>
                  </label>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            {uploadWaiting !== null ? (
              <GuardedButton
                disabled
                reason={uploadWaiting}
                variant="secondary"
                testId="dossier-televerser-confirmer"
              >
                {t("console.dossier.televerserConfirmer")}
              </GuardedButton>
            ) : (
              <GuardedButton
                variant="secondary"
                testId="dossier-televerser-confirmer"
                onClick={() => {
                  if (props.busy) return;
                  props.onFileOnBehalf(
                    chosen,
                    channel,
                    note.trim(),
                    files,
                    monthsAsked !== null && monthsAsked > 1
                      ? files.map((_, index) => months[index] ?? 1)
                      : undefined,
                  );
                  setFiles([]);
                  setMonths([]);
                  setFileInputKey((key) => key + 1);
                }}
              >
                {t("console.dossier.televerserConfirmer")}
              </GuardedButton>
            )}
            <span className="text-sm text-gray-500">{t("console.dossier.televerserProvenance")}</span>
          </div>
        </div>
      ) : null}

      <ul className="mt-2 divide-y divide-gray-100">
        {props.pieces.map((one) => {
          const link = props.links.get(one.code);
          const isReceived = one.status === "recue";
          const isRejected = one.status === "rejetee";
          const isMarked = one.status === "sansObjet";
          return (
            <li key={one.code} data-testid={`piece-${one.code}`} className="py-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2.5">
                  {isReceived ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                  ) : isMarked ? (
                    <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                  ) : (
                    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900">
                      {one.label}
                      {one.suppliedBy === "vendeur" ? (
                        <span
                          data-testid={`piece-${one.code}-vendeur`}
                          className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-sm font-medium text-gray-600"
                        >
                          {t("console.dossier.pieceDuVendeur")}
                        </span>
                      ) : null}
                    </p>
                    {/* `VISITE-SANS-OBJET`: the mark, its reason, its author and its day , the door's own words. */}
                    {isMarked && one.notApplicable !== null && one.notApplicable !== undefined ? (
                      <p className="text-sm text-gray-500" data-testid={`piece-${one.code}-provenance`}>
                        {t("console.dossier.pieceSansObjet")} : {one.notApplicable.reason} ·{" "}
                        {t("console.dossier.pieceSansObjetPar")} {one.notApplicable.by}{" "}
                        {t("console.dossier.pieceLe")} {day(one.notApplicable.at)}
                      </p>
                    ) : null}
                    {/* The provenance, in words, derived from the row itself. */}
                    {isReceived ? (
                      <p className="text-sm text-gray-500" data-testid={`piece-${one.code}-provenance`}>
                        {provenance(one)}
                        {one.note === null || one.note === undefined ? "" : ` · ${one.note}`}
                      </p>
                    ) : null}
                    {one.retiredOn === undefined || one.retiredOn === null ? null : (
                      <p className="text-sm text-gray-500" data-testid={`piece-${one.code}-retiree`}>
                        {t("console.dossier.pieceRetiree").replace("{date}", one.retiredOn)}
                      </p>
                    )}
                    {isRejected ? (
                      <p className="text-sm text-rose-600" data-testid={`piece-${one.code}-rejet`}>
                        {t("console.dossier.pieceRefusee")}
                        {one.rejectedAt === null || one.rejectedAt === undefined
                          ? ""
                          : ` ${t("console.dossier.pieceLe")} ${day(one.rejectedAt)}`}
                        {one.rejectionReason === null || one.rejectionReason === undefined
                          ? ""
                          : ` : ${one.rejectionReason}`}
                      </p>
                    ) : null}
                    {!isReceived && one.lienDepot !== null && one.lienDepot !== undefined ? (
                      <p className="text-sm text-indigo-600" data-testid={`piece-${one.code}-lien-envoye`}>
                        {one.lienDepot}
                        {one.relanceLe === null
                          ? ""
                          : ` ${t("console.dossier.pieceLe")} ${day(one.relanceLe)}`}
                      </p>
                    ) : null}
                    {!isReceived && !isRejected && (one.lienDepot === null || one.lienDepot === undefined) ? (
                      <p className="text-sm text-gray-500">{t("console.dossier.pieceManquante")}</p>
                    ) : null}
                    {/* `C7.4`: the civility, once read, beside the identity document. */}
                    {one.code === "pieceIdentite" && identityRead ? (
                      <p className="text-sm text-gray-500" data-testid="piece-identite-civilite">
                        {props.dossier.clientGender === "f"
                          ? t("console.dossier.civiliteMadame")
                          : t("console.dossier.civiliteMonsieur")}{" "}
                        {props.dossier.identityHolderName ?? ""}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  {!isReceived && atCollecte && one.action !== null && one.action !== undefined ? (
                    <button
                      type="button"
                      data-testid={`piece-${one.code}-relancer`}
                      disabled={props.busy}
                      onClick={() => {
                        props.onChase(one.code);
                      }}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40"
                    >
                      {one.action}
                    </button>
                  ) : null}
                  {/* `S4-6`: the mirror , the same door's shape, the vendor named instead of the client. */}
                  {!isReceived &&
                  atCollecte &&
                  one.vendorAction !== null &&
                  one.vendorAction !== undefined ? (
                    <button
                      type="button"
                      data-testid={`piece-${one.code}-relancer-vendeur`}
                      disabled={props.busy}
                      onClick={() => {
                        props.onChaseSeller(one.code);
                      }}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40"
                    >
                      {one.vendorAction}
                    </button>
                  ) : null}
                  {one.code === "visiteTechnique" && (isMarked || !isReceived) && mayMark ? (
                    <button
                      type="button"
                      data-testid={`piece-${one.code}-sans-objet${isMarked ? "-annuler" : ""}`}
                      disabled={props.busy}
                      onClick={() => {
                        setMarking(
                          marking?.code === one.code && marking.undo === isMarked
                            ? null
                            : { code: one.code, undo: isMarked },
                        );
                        setMarkReason("");
                      }}
                      className="text-sm font-medium text-gray-500 hover:text-indigo-600 disabled:opacity-40"
                    >
                      {isMarked
                        ? t("console.dossier.sansObjetAnnuler")
                        : t("console.dossier.sansObjetMarquer")}
                    </button>
                  ) : null}
                  {isReceived && mayReject ? (
                    <button
                      type="button"
                      data-testid={`piece-${one.code}-refuser`}
                      disabled={props.busy}
                      onClick={() => {
                        setRejecting(rejecting === one.code ? null : one.code);
                        setReason("");
                      }}
                      className="text-sm font-medium text-gray-500 hover:text-rose-600 disabled:opacity-40"
                    >
                      {t("console.dossier.pieceRefuser")}
                    </button>
                  ) : null}
                  {isReceived && mayJudge && CONSULTABLE_DOCUMENTS.includes(one.code) ? (
                    <button
                      type="button"
                      data-testid={`piece-${one.code}-voir`}
                      disabled={props.busy || consulting === one.code}
                      onClick={() => {
                        setConsulting(one.code);
                        void props
                          .onConsult(one.code)
                          .then((url) => {
                            if (url !== null) window.open(url, "_blank", "noopener,noreferrer");
                          })
                          .finally(() => {
                            setConsulting(null);
                          });
                      }}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40"
                    >
                      {t("console.dossier.piecesVoir")}
                    </button>
                  ) : null}
                  {one.code === "pieceIdentite" &&
                  isReceived &&
                  mayJudge &&
                  !identityRead &&
                  identityConsulted ? (
                    <button
                      type="button"
                      data-testid="piece-identite-lire"
                      disabled={props.busy}
                      onClick={() => {
                        setIdentityOpen(!identityOpen);
                      }}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40"
                    >
                      {t("console.dossier.civiliteLire")}
                    </button>
                  ) : null}
                </div>
              </div>

              {/* `C7.3`: the link, once, as the tour sheet's , a credential. */}
              {link === undefined ? null : (
                <CopyableLink
                  link={link}
                  testId={`piece-${one.code}-lien`}
                  title={t("console.dossier.lienTitre")}
                  text={t("console.dossier.lienTexte")}
                />
              )}

              {marking?.code === one.code ? (
                <div
                  data-testid={`piece-${one.code}-sans-objet-form`}
                  className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
                >
                  <label className="text-sm text-gray-500">
                    {t("console.dossier.sansObjetMotif")}
                    <input
                      data-testid={`piece-${one.code}-sans-objet-motif`}
                      value={markReason}
                      maxLength={300}
                      onChange={(event) => {
                        setMarkReason(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    {marking.undo
                      ? t("console.dossier.sansObjetTexteAnnuler")
                      : t("console.dossier.sansObjetTexte")}
                  </p>
                  {markWaiting !== null ? (
                    <GuardedButton
                      disabled
                      reason={markWaiting}
                      variant="secondary"
                      testId={`piece-${one.code}-sans-objet-confirmer`}
                    >
                      {marking.undo
                        ? t("console.dossier.sansObjetConfirmerAnnuler")
                        : t("console.dossier.sansObjetConfirmer")}
                    </GuardedButton>
                  ) : (
                    <GuardedButton
                      variant="secondary"
                      testId={`piece-${one.code}-sans-objet-confirmer`}
                      onClick={() => {
                        if (props.busy) return;
                        (marking.undo ? props.onUndoNotApplicable : props.onMarkNotApplicable)(
                          one.code,
                          markReason.trim(),
                        );
                        setMarking(null);
                      }}
                    >
                      {marking.undo
                        ? t("console.dossier.sansObjetConfirmerAnnuler")
                        : t("console.dossier.sansObjetConfirmer")}
                    </GuardedButton>
                  )}
                </div>
              ) : null}

              {rejecting === one.code ? (
                <div
                  data-testid={`piece-${one.code}-refus-form`}
                  className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
                >
                  <label className="text-sm text-gray-500">
                    {t("console.dossier.pieceRefusMotif")}
                    <input
                      data-testid={`piece-${one.code}-refus-motif`}
                      value={reason}
                      onChange={(event) => {
                        setReason(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <p className="mt-1 text-sm text-gray-500">{t("console.dossier.pieceRefusTexte")}</p>
                  {rejectWaiting !== null ? (
                    <GuardedButton
                      disabled
                      reason={rejectWaiting}
                      variant="secondary"
                      testId={`piece-${one.code}-refus-confirmer`}
                    >
                      {t("console.dossier.pieceRefusConfirmer")}
                    </GuardedButton>
                  ) : (
                    <GuardedButton
                      variant="secondary"
                      testId={`piece-${one.code}-refus-confirmer`}
                      onClick={() => {
                        if (props.busy) return;
                        props.onReject(one.code, reason.trim());
                        setRejecting(null);
                      }}
                    >
                      {t("console.dossier.pieceRefusConfirmer")}
                    </GuardedButton>
                  )}
                </div>
              ) : null}

              {one.code === "pieceIdentite" && identityOpen && !identityRead ? (
                <div
                  data-testid="piece-identite-form"
                  className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
                >
                  <p className="text-sm font-medium text-gray-700">{t("console.dossier.civiliteTitre")}</p>
                  <p className="mt-1 text-sm text-gray-500">{t("console.dossier.civiliteTexte")}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {(["f", "m"] as const).map((one) => (
                      <button
                        key={one}
                        type="button"
                        data-testid={`piece-identite-${one}`}
                        onClick={() => {
                          setGender(one);
                        }}
                        className={`rounded-lg border px-2.5 py-1.5 text-sm font-medium ${
                          gender === one
                            ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                            : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {one === "f"
                          ? t("console.dossier.civiliteMadame")
                          : t("console.dossier.civiliteMonsieur")}
                      </button>
                    ))}
                  </div>
                  <label className="mt-2 block text-sm text-gray-500">
                    {t("console.dossier.civiliteNom")}
                    <input
                      data-testid="piece-identite-nom"
                      value={holderName}
                      onChange={(event) => {
                        setHolderName(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                    />
                  </label>
                  {identityWaiting !== null ? (
                    <GuardedButton
                      disabled
                      reason={identityWaiting}
                      variant="secondary"
                      testId="piece-identite-confirmer"
                    >
                      {t("console.dossier.civiliteConfirmer")}
                    </GuardedButton>
                  ) : (
                    <GuardedButton
                      variant="secondary"
                      testId="piece-identite-confirmer"
                      onClick={() => {
                        if (props.busy) return;
                        if (gender === "") return;
                        props.onVerifyIdentity(gender, holderName.trim());
                        setIdentityOpen(false);
                      }}
                    >
                      {t("console.dossier.civiliteConfirmer")}
                    </GuardedButton>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div className="mt-3 border-t border-gray-100 pt-3">
        <p className="text-sm leading-relaxed text-gray-500">{t("console.dossier.piecesLienTexte")}</p>
        <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
          {t("console.dossier.piecesProvenanceTexte")}
        </p>
      </div>
    </section>
  );
}

// -------------------------------------------------------------- the report

export function ReportForm({
  grid,
  busy,
  onFile,
}: {
  readonly grid: readonly GridFamily[];
  readonly busy: boolean;
  readonly onFile: (entry: ReportEntry) => void;
}): React.JSX.Element {
  const [observed, setObserved] = useState<readonly string[]>([]);
  const [value, setValue] = useState("");
  const [observations, setObservations] = useState("");
  const [channel, setChannel] = useState("");
  // Round `C6` (`OD-33`): the cabinet's own paper, required , the document of record.
  const [paper, setPaper] = useState<File | null>(null);
  const amount = Number(value.replaceAll(/\s+/gu, ""));
  return (
    <div
      data-testid="dossier-rapport-form"
      className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
    >
      <p className="text-xs font-medium text-gray-700">{t("console.dossier.rapportTitre")}</p>
      {grid
        .filter((family) => family.isCheckable)
        .map((family) => (
          <fieldset key={family.code} className="mt-2">
            <legend
              className={"text-xs font-medium " + (family.isEliminating ? "text-rose-600" : "text-gray-700")}
            >
              {family.title}
            </legend>
            {family.criteria.map((criterion) => (
              <label key={criterion.code} className="mt-1 flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={observed.includes(criterion.code)}
                  onChange={(event) => {
                    setObserved(
                      event.target.checked
                        ? [...observed, criterion.code]
                        : observed.filter((code) => code !== criterion.code),
                    );
                  }}
                />
                {criterion.label}
              </label>
            ))}
          </fieldset>
        ))}
      <label className="mt-2 block text-xs text-gray-500">
        {t("console.dossier.rapportValeur")}
        <input
          data-testid="dossier-rapport-valeur"
          inputMode="numeric"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs tabular-nums"
        />
      </label>
      <label className="mt-2 block text-xs text-gray-500">
        {t("console.dossier.rapportObservations")}
        <textarea
          value={observations}
          onChange={(event) => {
            setObservations(event.target.value);
          }}
          className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
        />
      </label>
      {/* The reception channel is MANDATORY when the team enters it (`WF-CON-34`). */}
      <label className="mt-2 block text-xs text-gray-500">
        {t("console.dossier.rapportCanal")}
        <select
          data-testid="dossier-rapport-channel"
          value={channel}
          onChange={(event) => {
            setChannel(event.target.value);
          }}
          className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
        >
          <option value="">{DASH}</option>
          {[...CHANNELS.entries()].map(([code, key]) => (
            <option key={code} value={code}>
              {t(key)}
            </option>
          ))}
        </select>
      </label>
      <label className="mt-2 block text-xs text-gray-500">
        {t("console.dossier.rapportPapier")}
        <input
          type="file"
          data-testid="dossier-rapport-papier"
          accept="application/pdf,image/jpeg,image/png"
          onChange={(event) => {
            setPaper(event.target.files?.[0] ?? null);
          }}
          className="mt-1 block w-full text-xs"
        />
      </label>
      <p className="mt-2 text-xs italic text-gray-500">{t("console.dossier.rapportMention")}</p>
      <button
        type="button"
        data-testid="dossier-rapport-enregistrer"
        disabled={busy || channel === "" || !Number.isInteger(amount) || amount <= 0 || paper === null}
        {...(paper === null ? { title: t("console.dossier.rapportPapierRequis") } : {})}
        onClick={() => {
          if (paper === null) return;
          onFile({
            observed,
            estimatedValueAmount: amount,
            observations: observations.trim(),
            receptionChannel: channel,
            paper,
          });
        }}
        className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
      >
        {t("console.dossier.rapportEnregistrer")}
      </button>
    </div>
  );
}

// -------------------------------------------------------- the conversation

/**
 * `WF-CON-04` step 7: a side panel at full height, the thread bounded. READ-ONLY
 * (`P45`, D-1019-2): `messaging` sends nothing, so the panel carries no composer
 * and no « Bientôt disponible ` , a control without a door is not drawn.
 */
function ConversationPanel({
  journal,
  onClose,
}: {
  readonly journal: readonly JournalLine[];
  readonly onClose: () => void;
}): React.JSX.Element {
  return (
    <aside
      data-testid="dossier-conversation"
      className="fixed inset-y-0 right-0 flex w-96 flex-col border-l border-gray-200 bg-white shadow-xl"
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <h2 className="text-sm font-semibold">{t("console.dossier.conversationTitre")}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("console.dossier.fermer")}
          className="text-gray-500 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="flex-1 overflow-y-auto px-4 py-3">
        {journal.map((line) => (
          <li key={line.id} className="mb-3">
            <JournalMeta line={line} />
            <p className="text-sm text-gray-700">{line.body}</p>
          </li>
        ))}
      </ul>
    </aside>
  );
}

export interface SubmissionLine {
  readonly id: string;
  readonly bankName: string;
  readonly status: string;
  readonly submittedAt: string;
  readonly decidedAt: string | null;
  /** Round `L-3`, `M2`: the standing return, its target and its items; `null` while none. */
  readonly completion?: {
    readonly requestedAt: string;
    readonly clientMustAct: boolean;
    readonly items: readonly { readonly label: string; readonly documentCode: string | null }[];
  } | null;
  /** Round `L-3b`, `M2` (WF-CON-14): the refusal's motive, kept here and never on the buyer's read. */
  readonly refusal?: { readonly code: string; readonly label: string } | null;
  /** Round `L-4` (B14): how the bank's offer ended, from the row; the study withdrawn by the closure. */
  readonly offerEnd?: {
    readonly reason: "not-retained" | "client-declined" | "expired";
    readonly at: string;
  } | null;
  readonly withdrawnAt?: string | null;
}

/** B14: the row's own words for an ended offer or a withdrawn study; `null` while nothing ended. */
const endWordsOf = (one: SubmissionLine, board: boolean): string | null => {
  if (one.withdrawnAt)
    return t(board ? "console.suiviBancaire.detailRetiree" : "console.dossier.soumissionRetiree");
  const end = one.offerEnd ?? null;
  if (end === null) return null;
  if (end.reason === "expired")
    return fill(
      board ? "console.suiviBancaire.detailOffreExpiree" : "console.dossier.soumissionOffreExpiree",
      {
        date: day(end.at),
      },
    );
  if (end.reason === "client-declined")
    return t(board ? "console.suiviBancaire.detailOffreDeclinee" : "console.dossier.soumissionOffreDeclinee");
  return t(
    board ? "console.suiviBancaire.detailOffreNonRetenue" : "console.dossier.soumissionOffreNonRetenue",
  );
};

/** `L-3b`, M0 (d): what the submissions door answers , the rows and the closed list for the form. */
export interface SubmissionsRead {
  readonly submissions: readonly SubmissionLine[];
  readonly refusalReasons: readonly { readonly code: string; readonly label: string }[];
}

/**
 * Round `L-3`, `M2` (WF-CON-04 step 3, `hakili-console.jsx`'s « Suivi bancaire,
 * banque par banque »): **this board exists ONLY in the console** , the buyer
 * sees an aggregate, never which bank returned or refused. One row per bank:
 * the date, the detail, the status chip; a `retour` row exposes the drawing's
 * controls by its target , « Relancer le client sur la pièce » (the relance
 * door with the deposit link, WF-CON-06; then « Relance envoyée » disabled) and
 * « Pièce reçue, resoumettre » for a client-target return, « Traiter en interne
 * et resoumettre » for an internal one , with the visibility note beside them.
 * The footer restates the rule. Every figure on it is the door's.
 */
export function BankFollowUp(props: {
  readonly submissions: readonly SubmissionLine[];
  /** R-B: the pièces' rows , the last relance of the requested document, the fact the control reads. */
  readonly pieces: readonly PieceView[];
  readonly busy: boolean;
  readonly onChase: (documentCode: string) => void;
  readonly onResubmit: (submissionId: string) => void;
}): React.JSX.Element | null {
  if (props.submissions.length === 0) return null;
  const studying = props.submissions.filter(
    (one) => (one.status === "etude" || one.status === "retour") && !one.withdrawnAt,
  ).length;
  const offers = props.submissions.filter((one) => one.status === "offre").length;
  // The refused chip's word is the domain's own label for the bank's `refuse` stage, never a literal here.
  const chip = (status: string, ended: boolean): { readonly key: string | null; readonly tone: string } =>
    ended
      ? { key: "console.suiviBancaire.chipTerminee", tone: "bg-gray-100 text-gray-600" }
      : status === "retour"
        ? { key: "console.suiviBancaire.chipRetour", tone: "bg-amber-50 text-amber-700" }
        : status === "offre"
          ? { key: "console.suiviBancaire.chipOffre", tone: "bg-emerald-50 text-emerald-700" }
          : status === "refus"
            ? { key: null, tone: "bg-rose-50 text-rose-700" }
            : { key: "console.suiviBancaire.chipEtude", tone: "bg-indigo-50 text-indigo-700" };
  // `L-3b`, `M2` (WF-CON-14's criterion): the refused row keeps its motive, the console's own.
  const detailOf = (one: SubmissionLine): string =>
    (endWordsOf(one, true) ?? null) !== null
      ? (endWordsOf(one, true) ?? "")
      : one.status === "retour" && one.completion
        ? one.completion.items.map((item) => item.label).join(" ; ")
        : one.status === "offre"
          ? t("console.suiviBancaire.detailOffre")
          : one.status === "refus"
            ? one.refusal
              ? fill("console.suiviBancaire.detailRefusMotif", { motif: one.refusal.label })
              : t("console.suiviBancaire.detailRefus")
            : t("console.suiviBancaire.detailEtude");
  return (
    <section
      className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white"
      data-testid="suivi-bancaire"
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <h3 className="text-sm font-semibold text-gray-900">{t("console.suiviBancaire.titre")}</h3>
        <span className="text-xs text-gray-500" data-testid="suivi-bancaire-etudes">
          {fill(
            studying === 1
              ? "console.suiviBancaire.etudesEnCours"
              : "console.suiviBancaire.etudesEnCoursPluriel",
            {
              n: String(studying),
            },
          )}
        </span>
      </div>
      <ul className="divide-y divide-gray-50">
        {props.submissions.map((one) => {
          const word = chip(one.status, endWordsOf(one, true) !== null);
          const document =
            one.completion?.items.find((item) => item.documentCode !== null)?.documentCode ?? null;
          // R-B (the architect's ruling on `L-3`): « Relance envoyée » is the FILE's fact , the pièce's own
          // last relance, later than the return's request , never the session's memory of a click.
          const lastChase =
            document === null ? null : (props.pieces.find((one) => one.code === document)?.relanceLe ?? null);
          const requestedAt = one.completion?.requestedAt ?? null;
          const chased =
            lastChase !== null &&
            requestedAt !== null &&
            new Date(lastChase).getTime() >= new Date(requestedAt).getTime();
          return (
            <li key={one.id} className="px-5 py-3.5" data-testid={`suivi-bancaire-${one.status}`}>
              <div className="flex items-center gap-4">
                <div className="w-40 shrink-0">
                  <p className="text-sm font-medium text-gray-900">{one.bankName}</p>
                  <p className="text-xs text-gray-500">
                    {fill("console.suiviBancaire.le", { date: day(one.decidedAt ?? one.submittedAt) })}
                  </p>
                </div>
                <p className="flex-1 text-sm leading-snug text-gray-600" data-testid="suivi-bancaire-detail">
                  {detailOf(one)}
                </p>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${word.tone}`}>
                  {word.key === null ? BANK_FILE_STAGE_LABELS.refuse : t(word.key)}
                </span>
              </div>
              {/* `L-4`: a row that ended (withdrawn, the offer ended) offers no chase and no resubmission. */}
              {one.status === "retour" && endWordsOf(one, true) === null ? (
                <div className="ml-44 mt-2.5 flex flex-wrap items-center gap-2">
                  {one.completion?.clientMustAct !== false ? (
                    <>
                      {document === null ? null : chased ? (
                        <>
                          <DoneLine testId="suivi-bancaire-relance-envoyee">
                            {t("console.suiviBancaire.relanceEnvoyee")}
                          </DoneLine>
                          <span className="text-xs text-gray-500" data-testid="suivi-bancaire-relance-le">
                            {fill("console.suiviBancaire.le", { date: day(lastChase) })}
                          </span>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={props.busy}
                          onClick={() => {
                            props.onChase(document);
                          }}
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                          data-testid="suivi-bancaire-relancer"
                        >
                          {t("console.suiviBancaire.relancerPiece")}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={props.busy}
                        onClick={() => {
                          props.onResubmit(one.id);
                        }}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        data-testid="suivi-bancaire-resoumettre"
                      >
                        {t("console.suiviBancaire.pieceRecue")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      disabled={props.busy}
                      onClick={() => {
                        props.onResubmit(one.id);
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      data-testid="suivi-bancaire-interne"
                    >
                      {t("console.suiviBancaire.traiterInterne")}
                    </button>
                  )}
                  <span className="text-xs text-gray-500" data-testid="suivi-bancaire-note">
                    {one.completion?.clientMustAct !== false
                      ? t("console.suiviBancaire.noteClient")
                      : t("console.suiviBancaire.noteInterne")}
                  </span>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="px-5 py-3 text-sm leading-relaxed text-gray-500" data-testid="suivi-bancaire-pied">
        {fill("console.suiviBancaire.pied", {
          banques: fill(
            props.submissions.length === 1
              ? "console.suiviBancaire.banqueSaisie"
              : "console.suiviBancaire.banquesSaisies",
            { n: String(props.submissions.length) },
          ),
          offres: fill(
            offers <= 1 ? "console.suiviBancaire.offreRecue" : "console.suiviBancaire.offresRecues",
            {
              n: String(offers),
            },
          ),
        })}
      </p>
    </section>
  );
}

/** B10: the bank's paper as it wrote it , every figure typed, none computed by the console. */
export interface RecordedOfferInput {
  readonly durationMonths: number;
  readonly nominalRatePct: number;
  readonly depositPct: number;
  readonly amountVehicle: number;
  readonly amountWarranty: number;
  readonly financesWarranty: boolean;
  readonly feeBankAmount: number;
  readonly feeHakiliAmount: number;
  readonly feeBaseAmount: number;
  readonly financesBankFee: boolean;
  readonly financesHakiliFee: boolean;
  readonly instalmentAmount: number;
  readonly insuranceInstalmentAmount: number;
  readonly taegPct: number;
  readonly validUntil: string;
  readonly letterReference: string;
  /** R-I (`L-3`, `M0 (d)`): the bank's agency as its letter names it; empty when the letter names none. */
  readonly branchLabel: string;
}

const SUBMISSION_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["etude", "console.dossier.soumissionEtude"],
  ["retour", "console.dossier.soumissionRetour"],
  ["offre", "console.dossier.soumissionOffre"],
  ["refus", "console.dossier.soumissionRefus"],
]);

function SoumisPanel({
  submissions,
  refusalReasons,
  recordOffer,
  recordRefusal,
  onRecorded,
  onAllRefused,
  busy,
}: {
  readonly submissions: readonly SubmissionLine[];
  readonly refusalReasons: readonly { readonly code: string; readonly label: string }[];
  readonly recordOffer?: DossierDoors["recordOffer"] | undefined;
  readonly recordRefusal?: DossierDoors["recordRefusal"] | undefined;
  readonly onRecorded?: (() => void) | undefined;
  readonly onAllRefused?: (() => void) | undefined;
  readonly busy: boolean;
}): React.JSX.Element {
  const [recording, setRecording] = useState<SubmissionLine | null>(null);
  const [refusing, setRefusing] = useState<SubmissionLine | null>(null);
  // Round `L-3` (found at the pairs): a bank that returned the file has not answered on paper; B10 waits for `etude`.
  const open = submissions.filter((one) => one.status === "etude" && !one.withdrawnAt);
  // `L-3b`, M0 (d): a refusal may be recorded on a bank still studying or one that returned the file.
  const refusable = submissions.filter(
    (one) => (one.status === "etude" || one.status === "retour") && !one.withdrawnAt,
  );
  // WF-CON-14 step 4, 05 §2.6.9: the declaration waits for every submission at `refus` , read when
  // asked (A47), re-read by the door; a file nobody engaged has no bank to have refused it.
  const allRefused = submissions.length > 0 && submissions.every((one) => one.status === "refus");
  return (
    <div data-testid="dossier-soumis" className="mb-1">
      {recording === null ? null : (
        <RecordOfferForm
          submission={recording}
          recordOffer={recordOffer}
          onClose={() => {
            setRecording(null);
          }}
          onRecorded={() => {
            setRecording(null);
            onRecorded?.();
          }}
        />
      )}
      {refusing === null ? null : (
        <RecordRefusalForm
          submission={refusing}
          reasons={refusalReasons}
          recordRefusal={recordRefusal}
          onClose={() => {
            setRefusing(null);
          }}
          onRecorded={() => {
            setRefusing(null);
            onRecorded?.();
          }}
        />
      )}
      <p className="text-xs font-medium text-gray-700">{t("console.dossier.soumisTitre")}</p>
      <ul className="mt-1.5 space-y-1" data-testid="dossier-soumis-banques">
        {submissions.map((one) => {
          const key = SUBMISSION_WORDS.get(one.status);
          const answered = one.status === "offre" || one.status === "refus";
          return (
            <li
              key={one.id}
              className="flex items-center gap-2 text-sm text-gray-600"
              data-testid={`dossier-soumission-${one.status}`}
            >
              {answered ? (
                <CheckCircle2
                  className={`h-4 w-4 shrink-0 ${one.status === "offre" ? "text-emerald-600" : "text-rose-500"}`}
                  aria-hidden="true"
                />
              ) : (
                <Clock className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
              )}
              <span className="font-medium text-gray-900">{one.bankName}</span>
              <span>
                {endWordsOf(one, false) ??
                  (key === undefined ? one.status : fill(key, { date: day(one.decidedAt) }))}
              </span>
            </li>
          );
        })}
        {submissions.length === 0 ? (
          <li className="text-sm text-gray-500" data-testid="dossier-soumis-aucune">
            {t("console.dossier.soumisAucune")}
          </li>
        ) : null}
        <li className="flex items-center gap-2 text-sm text-gray-500" data-testid="dossier-soumis-retenue">
          <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-200" aria-hidden="true" />
          {t("console.dossier.soumisRetenue")}
        </li>
      </ul>
      {/* B10: « Enregistrer une offre reçue » opens the form over the veil, per bank still studying. */}
      <div className="mt-3 flex flex-wrap gap-2">
        {open.map((one) => (
          <button
            key={one.id}
            type="button"
            data-testid="dossier-enregistrer-offre"
            disabled={recordOffer === undefined}
            onClick={() => {
              setRecording(one);
            }}
            className="w-fit rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {ACTION.recordOffer} · {one.bankName}
          </button>
        ))}
        {/* `L-3b`, M0 (d), B13: « Enregistrer un refus » per bank that has not answered, the
            form over the veil with the motive; the door refuses a bank that already answered. */}
        {refusable.map((one) => (
          <button
            key={`refus-${one.id}`}
            type="button"
            data-testid="dossier-enregistrer-refus"
            disabled={recordRefusal === undefined || busy}
            onClick={() => {
              setRefusing(one);
            }}
            className="w-fit rounded-xl border border-rose-100 bg-white px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-40"
          >
            {ACTION.recordRefusal} · {one.bankName}
          </button>
        ))}
        {/* `M0 (d)`: the acceptance is the buyer's own act, by his code (WF-CLI-22). `SWEEP-AA` (D-1019-2): it is
            not her door, so no control is drawn (it was a greyed one with an amber reason). */}
        {/* WF-CON-14 step 4 (`P1-LND-015`): the declaration, disabled with 05 §2.6.9's own
            sentence while one bank stands; the door re-reads the guard and refuses in the same words. */}
        {allRefused && onAllRefused !== undefined ? (
          <button
            type="button"
            data-testid="dossier-toutes-refuse"
            disabled={busy}
            onClick={onAllRefused}
            className="w-fit rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
          >
            {ACTION.allBanksRefused}
          </button>
        ) : (
          <GuardedButton disabled reason={t("console.refus.toutesMotif")} testId="dossier-toutes-refuse">
            {ACTION.allBanksRefused}
          </GuardedButton>
        )}
      </div>
    </div>
  );
}

/**
 * Round `L-3b`, `M0 (d)`, B13 (a drawing in words): **the bank's refusal recorded
 * by hand, over the veil, with its motive.** The drawing's « Enregistrer un
 * refus » records with no field; WF-CON-14 says « with its motive » and the
 * door (`P1-LND-013`'s) refuses a refusal without a code , so the control opens
 * B10's sibling: the sentence, « Motif du refus » over the closed list the door
 * serves, opening on « Choisir un motif », the confirm disabled until chosen.
 */
function RecordRefusalForm({
  submission,
  reasons,
  recordRefusal,
  onClose,
  onRecorded,
}: {
  readonly submission: SubmissionLine;
  readonly reasons: readonly { readonly code: string; readonly label: string }[];
  readonly recordRefusal?: DossierDoors["recordRefusal"] | undefined;
  readonly onClose: () => void;
  readonly onRecorded: () => void;
}): React.JSX.Element {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  return (
    <Modal
      open
      title={`${ACTION.recordRefusal} · ${submission.bankName}`}
      onClose={onClose}
      closeLabel={t("console.dossier.fermer")}
    >
      <form
        data-testid="dossier-refus-formulaire"
        onSubmit={(event) => {
          event.preventDefault();
          if (recordRefusal === undefined || reason === "") return;
          setBusy(true);
          setRefusal(null);
          void recordRefusal(submission.id, reason)
            .then(onRecorded)
            .catch((failure: unknown) => {
              setRefusal(failureWords(failure));
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <p className="text-sm text-gray-500">{t("console.refus.phrase")}</p>
        <label className="mt-3 block text-xs font-medium text-gray-600">
          {t("console.refus.motif")}
          <select
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
            data-testid="dossier-refus-motif"
          >
            <option value="">{t("console.refus.choisir")}</option>
            {reasons.map((one) => (
              <option key={one.code} value={one.code}>
                {one.label}
              </option>
            ))}
          </select>
        </label>
        {refusal === null ? null : (
          <p role="alert" data-testid="dossier-refus-formulaire-refus" className="mt-2 text-sm text-rose-600">
            {refusal}
          </p>
        )}
        <div className="mt-4 flex justify-end">
          <button
            type="submit"
            disabled={busy || reason === "" || recordRefusal === undefined}
            data-testid="dossier-refus-confirmer"
            className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
          >
            {t("console.refus.confirmer")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** A whole number typed from the bank's paper; `null` while the field is empty or not a number. */
const wholeOf = (text: string): number | null => {
  const digits = text.replaceAll(/[\s\u202f]/gu, "");
  if (!/^\d+$/u.test(digits)) return null;
  return Number(digits);
};
const rateOf = (text: string): number | null => {
  const normalised = text.replace(",", ".").trim();
  if (!/^\d+(?:\.\d)?$/u.test(normalised)) return null;
  return Number(normalised);
};

/**
 * Round `L-2`, `M3`, B10 (a drawing in words): **the bank's offer recorded by
 * hand, over the veil, the figures as the bank wrote them.** Nothing here is
 * computed: the console types what the paper says and the door keeps it as
 * the offer's row, so a bank that answers on paper has an offer the buyer can
 * compare. The letter's reference stands in for the paper until a document of
 * that reason can be filed; the attachment is recorded as a debt on the
 * submission's event.
 */
function RecordOfferForm({
  submission,
  recordOffer,
  onClose,
  onRecorded,
}: {
  readonly submission: SubmissionLine;
  readonly recordOffer?: DossierDoors["recordOffer"] | undefined;
  readonly onClose: () => void;
  readonly onRecorded: () => void;
}): React.JSX.Element {
  const [fields, setFields] = useState<Record<string, string>>({});
  const [flags, setFlags] = useState({
    financesWarranty: false,
    financesBankFee: false,
    financesHakiliFee: false,
  });
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const field = (name: string): string => fields[name] ?? "";
  const set = (name: string, value: string): void => {
    setFields({ ...fields, [name]: value });
  };
  const whole = (name: string): number | null => wholeOf(field(name));
  const input = {
    durationMonths: whole("durationMonths"),
    nominalRatePct: rateOf(field("nominalRatePct")),
    depositPct: whole("depositPct"),
    amountVehicle: whole("amountVehicle"),
    amountWarranty: field("amountWarranty") === "" ? 0 : whole("amountWarranty"),
    feeBankAmount: whole("feeBankAmount"),
    feeHakiliAmount: whole("feeHakiliAmount"),
    instalmentAmount: whole("instalmentAmount"),
    insuranceInstalmentAmount:
      field("insuranceInstalmentAmount") === "" ? 0 : whole("insuranceInstalmentAmount"),
    taegPct: rateOf(field("taegPct")),
    validUntil: /^\d{4}-\d{2}-\d{2}$/u.test(field("validUntil")) ? field("validUntil") : null,
  };
  const complete = Object.values(input).every((value) => value !== null);
  const MONEY_FIELDS: readonly { readonly name: string; readonly key: Parameters<typeof t>[0] }[] = [
    { name: "amountVehicle", key: "console.dossier.offreMontantVehicule" },
    { name: "amountWarranty", key: "console.dossier.offreGarantie" },
    { name: "feeBankAmount", key: "console.dossier.offreFraisBanque" },
    { name: "feeHakiliAmount", key: "console.dossier.offreFraisHakili" },
    { name: "instalmentAmount", key: "console.dossier.offreMensualite" },
    { name: "insuranceInstalmentAmount", key: "console.dossier.offreAssurance" },
  ];
  const cell =
    "mt-1 block w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400";
  return (
    <Modal
      open
      title={`${ACTION.recordOffer} · ${submission.bankName}`}
      onClose={onClose}
      closeLabel={t("console.dossier.fermer")}
      className="max-w-2xl"
    >
      <form
        data-testid="dossier-offre-formulaire"
        onSubmit={(event) => {
          event.preventDefault();
          if (!complete || recordOffer === undefined) return;
          void (async () => {
            setBusy(true);
            setRefusal(null);
            try {
              await recordOffer(submission.id, {
                durationMonths: input.durationMonths ?? 0,
                nominalRatePct: input.nominalRatePct ?? 0,
                depositPct: input.depositPct ?? 0,
                amountVehicle: input.amountVehicle ?? 0,
                amountWarranty: input.amountWarranty ?? 0,
                financesWarranty: flags.financesWarranty,
                feeBankAmount: input.feeBankAmount ?? 0,
                feeHakiliAmount: input.feeHakiliAmount ?? 0,
                // The base both fees were computed on is the bank's own paper's: the amount it finances for the car.
                feeBaseAmount: input.amountVehicle ?? 0,
                financesBankFee: flags.financesBankFee,
                financesHakiliFee: flags.financesHakiliFee,
                instalmentAmount: input.instalmentAmount ?? 0,
                insuranceInstalmentAmount: input.insuranceInstalmentAmount ?? 0,
                taegPct: input.taegPct ?? 0,
                validUntil: input.validUntil ?? "",
                letterReference: field("letterReference").trim(),
                branchLabel: field("branchLabel").trim(),
              });
              onRecorded();
            } catch (failure) {
              setRefusal(
                (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
              );
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        <p className="mb-3 text-sm text-gray-500">{t("console.dossier.offreFormulaireSous")}</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-gray-600">
            {t("console.dossier.offreDuree")}
            <input
              data-testid="offre-duree"
              value={field("durationMonths")}
              onChange={(e) => {
                set("durationMonths", e.target.value);
              }}
              className={cell}
              inputMode="numeric"
            />
          </label>
          <label className="block text-xs font-medium text-gray-600">
            {t("console.dossier.offreTaux")}
            <input
              data-testid="offre-taux"
              value={field("nominalRatePct")}
              onChange={(e) => {
                set("nominalRatePct", e.target.value);
              }}
              className={cell}
              inputMode="decimal"
            />
          </label>
          <label className="block text-xs font-medium text-gray-600">
            {t("console.dossier.offreApport")}
            <input
              data-testid="offre-apport"
              value={field("depositPct")}
              onChange={(e) => {
                set("depositPct", e.target.value);
              }}
              className={cell}
              inputMode="numeric"
            />
          </label>
          <label className="block text-xs font-medium text-gray-600">
            {t("console.dossier.offreTaeg")}
            <input
              data-testid="offre-taeg"
              value={field("taegPct")}
              onChange={(e) => {
                set("taegPct", e.target.value);
              }}
              className={cell}
              inputMode="decimal"
            />
          </label>
          {MONEY_FIELDS.map((one) => (
            <label key={one.name} className="block text-xs font-medium text-gray-600">
              {t(one.key)}
              <input
                data-testid={`offre-${one.name}`}
                value={field(one.name)}
                onChange={(e) => {
                  set(one.name, e.target.value);
                }}
                className={cell}
                inputMode="numeric"
              />
            </label>
          ))}
          <label className="block text-xs font-medium text-gray-600">
            {t("console.dossier.offreValidite")}
            <input
              data-testid="offre-validite"
              type="date"
              value={field("validUntil")}
              onChange={(e) => {
                set("validUntil", e.target.value);
              }}
              className={cell}
            />
          </label>
          <label className="block text-xs font-medium text-gray-600">
            {t("console.dossier.offreLettre")}
            <input
              data-testid="offre-lettre"
              value={field("letterReference")}
              onChange={(e) => {
                set("letterReference", e.target.value);
              }}
              className={cell}
            />
          </label>
          <label className="block text-xs font-medium text-gray-600">
            {t("console.dossier.offreAgence")}
            <input
              data-testid="offre-agence"
              value={field("branchLabel")}
              onChange={(e) => {
                set("branchLabel", e.target.value);
              }}
              maxLength={120}
              className={cell}
            />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-700">
          {(
            [
              ["financesBankFee", "console.dossier.offreFraisBanqueFinances"],
              ["financesHakiliFee", "console.dossier.offreFraisHakiliFinances"],
              ["financesWarranty", "console.dossier.offreGarantieFinancee"],
            ] as const
          ).map(([name, key]) => (
            <label key={name} className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid={`offre-${name}`}
                checked={flags[name]}
                onChange={(e) => {
                  setFlags({ ...flags, [name]: e.target.checked });
                }}
              />
              {t(key)}
            </label>
          ))}
        </div>
        <p className="mt-3 text-sm text-gray-500">{t("console.dossier.offreLettreNote")}</p>
        {refusal === null ? null : (
          <p role="alert" data-testid="dossier-offre-refus" className="mt-2 text-sm text-rose-600">
            {refusal}
          </p>
        )}
        <button
          type="submit"
          data-testid="dossier-offre-enregistrer"
          disabled={!complete || busy || recordOffer === undefined}
          className="mt-4 w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
        >
          {t("console.dossier.offreEnregistrer")}
        </button>
      </form>
    </Modal>
  );
}

/**
 * Round `L-2`, `M3`: **the file at « Offre ».** The checklist's first line reads
 * the buyer's acceptance , the event moved the file here and named the bank.
 *
 * Round `T-1`, `M2` (the insurance parked for the pilot by the founder's word; 02 §2.4.1
 * and WF-CON-15 amended by record, both texts): the second line reads « Assurance à la
 * charge du client, attestation à présenter à la signature », and the panel's control is
 * « Passer aux conditions préalables » , the machine's own row from `offre`, guarded by
 * the retained offer, its journal line the door's. The placement's control is drawn by
 * nobody: a parked feature is not a greyed control with a round in its title.
 */
function OffrePanel(props: {
  readonly dossier: DossierDetail;
  readonly busy: boolean;
  readonly onAdvance: (action: string) => void;
}): React.JSX.Element {
  const { dossier } = props;
  return (
    <div data-testid="dossier-offre" className="mb-1">
      <p className="text-xs font-medium text-gray-700">{t("console.dossier.offreTitre")}</p>
      <ul className="mt-1.5 space-y-1">
        <li className="flex items-center gap-2 text-sm text-gray-600" data-testid="dossier-offre-retenue">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
          <span>
            {t("console.dossier.offreRetenueLigne")}
            {dossier.mainBankName === undefined || dossier.mainBankName === null
              ? ""
              : ` : ${dossier.mainBankName}`}
          </span>
        </li>
        <li className="flex items-center gap-2 text-sm text-gray-500" data-testid="dossier-offre-assurance">
          <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-200" aria-hidden="true" />
          {t("console.dossier.offreAssuranceClient")}
        </li>
      </ul>
      <div className="mt-3">
        <button
          type="button"
          data-testid="dossier-passer-conditions"
          disabled={props.busy}
          onClick={() => {
            props.onAdvance(ACTION.proceedToConditions);
          }}
          className="w-fit rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
        >
          {ACTION.proceedToConditions}
        </button>
      </div>
    </div>
  );
}

/**
 * Round `T-1b`, `M2` (`P2-CNW-003`, `P2-DOS-002`; WF-CON-18 steps 1 to 6): **the panel at « Conditions ».**
 * Each of Hakili's four a chip « À lever : … » / « Levée : … » calling its own door (`lift`, then
 * `reopen` when lifted); the observed one (the fee) disabled with `T-1c` in its title until its fact
 * exists; « Toutes les conditions sont levées » disabled with « N conditions restantes : <liste> » in the
 * drawing's French (`enumererFr`, the texts in lower case, the singular for one), and, once every
 * condition is lifted, greyed with `T-2` (the advance to `decaisse` and its journal line are T-2's);
 * « Éditer le bon de retrait » a separate control greyed with `T-2` and the record's reason shape; the
 * bank's five read as information , « Côté banque : N/5 levées », each with its state and date.
 */
export const remainingWords = (labels: readonly string[]): string => {
  // The label's first letter only: « Frais de dossier Hakili réglés » keeps its proper noun (the designer's D).
  const lower = labels.map((one) => `${one.charAt(0).toLowerCase()}${one.slice(1)}`);
  const last = lower.at(-1) ?? "";
  const head = lower.slice(0, -1);
  const liste = head.length === 0 ? last : `${head.join(", ")} et ${last}`;
  return `${String(lower.length)} ${
    lower.length === 1 ? t("console.dossier.conditionRestante") : t("console.dossier.conditionsRestantes")
  } : ${liste}`;
};

function ConditionsPanel(props: {
  readonly conditions: readonly DossierConditionView[] | null;
  readonly bankConditions: BankConditionsView | null;
  readonly settlement: SettlementLineView | null;
  readonly busy: boolean;
  readonly onLift: ((cle: string, how: "lift" | "reopen") => void) | undefined;
  readonly onTransmit: () => void;
}): React.JSX.Element {
  const conditions = props.conditions ?? [];
  const remaining = conditions.filter((one) => !one.satisfied);
  const settlement = props.settlement;
  return (
    <div data-testid="dossier-conditions" className="mb-1">
      <div className="flex flex-wrap gap-2">
        {conditions.map((condition) =>
          condition.observed ? (
            // WF-CON-18 step 2: an observed condition is not ticked by hand , its fact is `T-1c`'s. `SWEEP-AA`
            // (D-1019-2/3): not her door, so no control; once Hakili has observed it, the fact is a status line.
            condition.satisfied ? (
              <DoneLine key={condition.key} testId={`dossier-condition-${condition.key}`}>
                {t("console.dossier.conditionLevee")} {condition.label}
              </DoneLine>
            ) : null
          ) : (
            <button
              key={condition.key}
              type="button"
              data-testid={`dossier-condition-${condition.key}`}
              data-satisfied={condition.satisfied ? "true" : "false"}
              disabled={props.busy || props.onLift === undefined}
              onClick={() => {
                props.onLift?.(condition.key, condition.satisfied ? "reopen" : "lift");
              }}
              className={
                "rounded-lg border px-2.5 py-1.5 text-xs font-medium disabled:opacity-60 " +
                (condition.satisfied
                  ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")
              }
            >
              {condition.satisfied
                ? t("console.dossier.conditionLevee")
                : t("console.dossier.conditionALever")}{" "}
              {condition.label}
            </button>
          ),
        )}
      </div>
      {props.bankConditions === null ? null : (
        <div className="mt-3 rounded-xl bg-gray-50 px-3.5 py-3" data-testid="dossier-conditions-banque">
          <p className="text-xs font-medium text-gray-700" data-testid="dossier-conditions-banque-compte">
            {fill("console.dossier.coteBanque", {
              n: String(props.bankConditions.liftedCount),
              total: String(props.bankConditions.total),
            })}
          </p>
          <ul className="mt-1.5 space-y-1">
            {props.bankConditions.conditions.map((one) => (
              <li
                key={one.key}
                className="flex items-center gap-2 text-xs text-gray-600"
                data-testid={`dossier-conditions-banque-${one.key}`}
              >
                {one.state === "levee" ? (
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
                ) : (
                  <Clock className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                )}
                <span>
                  {one.label}
                  {one.state === "levee" && one.liftedAt !== null
                    ? `, ${fill("console.dossier.leveeLe", { date: day(one.liftedAt) })}`
                    : ""}
                  {one.liftedBy === "hakili" ? `, ${t("console.dossier.constateParHakili")}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {remaining.length > 0 ? (
          <GuardedButton
            disabled
            reason={remainingWords(remaining.map((one) => one.label))}
            variant="primary"
            testId="dossier-conditions-toutes"
          >
            {t("console.dossier.transmettreOrdre")}
          </GuardedButton>
        ) : settlement !== null ? (
          // `T-2`: the ordre stands , the bank's analyst transmitted it by his door; the chargée's line is written.
          <DoneLine testId="dossier-conditions-transmise">
            {fill("console.dossier.ordreTransmisMotif", { reference: settlement.reference })}
          </DoneLine>
        ) : (
          // `T-2` (WF-CON-18 step 4): the chargée's transmission, live once the four are lifted.
          <button
            type="button"
            disabled={props.busy}
            data-testid="dossier-conditions-toutes"
            onClick={props.onTransmit}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
          >
            {t("console.dossier.transmettreOrdre")}
          </button>
        )}
        {/* `T-2`: « Éditer le bon de retrait » greyed with the settlement's own reason while a leg stands
            (WF-CON-18's two sentences), with the bon's reference once the platform issued it (R81: no PDF). */}
        {settlement?.voucher !== null && settlement?.voucher !== undefined ? (
          <DoneLine testId="dossier-bon-retrait-emis">
            {fill("console.dossier.bonEmis", {
              reference: settlement.voucher.reference,
              date: day(settlement.voucher.issuedAt),
            })}
          </DoneLine>
        ) : (
          <GuardedButton disabled reason={voucherReason(settlement)} testId="dossier-bon-retrait">
            {t("console.dossier.editerBonRetrait")}
          </GuardedButton>
        )}
      </div>
    </div>
  );
}

/** WF-CON-18's guard sentences beside « Éditer le bon de retrait », the settlement's own state read. */
function voucherReason(settlement: SettlementLineView | null): string {
  if (settlement === null) return t("console.dossier.bonRetraitAucunOrdre");
  if (settlement.missing === "hakili") return t("console.dossier.bonBloqueHakili");
  if (settlement.missing === "concessionnaire") return t("console.dossier.bonBloqueConcessionnaire");
  return t("console.dossier.bonBloqueDeux");
}

/**
 * `T-2` (WF-CON-18 step 5): the règlement card, one row per leg , the dealer's 90 % declared by
 * him (WF-DLR-11), Hakili's 10 % observed on its own account , the named state beneath, never an
 * error badge; the bon's state on the second leg.
 */
function SettlementCard(props: { readonly settlement: SettlementLineView }): React.JSX.Element {
  const { settlement } = props;
  const complete = settlement.status === "complet";
  return (
    <div
      data-testid="dossier-reglement"
      className="mb-4 rounded-2xl border border-gray-200 bg-white px-5 py-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t("console.dossier.reglementTitre")}</h3>
        <span
          data-testid="dossier-reglement-statut"
          className={
            "rounded-full px-2.5 py-1 text-xs font-medium " +
            (complete ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")
          }
        >
          {complete
            ? t("console.reglements.complet")
            : settlement.status === "attenteVirements"
              ? t("console.reglements.attente")
              : t("console.reglements.partiel")}
        </span>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {settlement.reference}
        {settlement.voucher === null
          ? ""
          : ` · ${fill("console.reglements.bon", { reference: settlement.voucher.reference })}`}
      </p>
      <table className="mt-3 w-full text-xs" data-testid="dossier-reglement-volets">
        <tbody>
          <tr data-testid="dossier-reglement-concessionnaire">
            <td className="py-1 text-gray-700">{t("console.reglements.voletConcessionnaire")}</td>
            <td className="py-1 text-right tabular-nums text-gray-900">
              {formatMoney(settlement.dealerAmount)}
            </td>
            <td className="py-1 pl-3 text-gray-600">{legWords(settlement, "concessionnaire")}</td>
          </tr>
          <tr data-testid="dossier-reglement-hakili">
            <td className="py-1 text-gray-700">{t("console.reglements.voletHakili")}</td>
            <td className="py-1 text-right tabular-nums text-gray-900">
              {formatMoney(settlement.hakiliAmount)}
            </td>
            <td className="py-1 pl-3 text-gray-600">{legWords(settlement, "hakili")}</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-2 text-sm text-gray-500" data-testid="dossier-reglement-bon">
        {settlement.voucher === null
          ? fill("console.dossier.bonBloque", { motif: settlement.blockedReason ?? "" })
          : settlement.voucher.presentedAt === null
            ? fill("console.dossier.bonEmis", {
                reference: settlement.voucher.reference,
                date: day(settlement.voucher.issuedAt),
              })
            : fill("console.dossier.bonPresente", {
                reference: settlement.voucher.reference,
                date: day(settlement.voucher.presentedAt),
              })}
      </p>
    </div>
  );
}

/**
 * Round `T-1`, `M2` (WF-CON-17's guard, the insurance parked): the « Assurance du dossier »
 * card, from `soumis` onwards , the explicit « Aucune assurance à ce stade du dossier. » and
 * the pilot's rule beneath it. No insurer, no premium, no commission, no chase.
 */
const INSURANCE_CARD_FROM = ["soumis", "offre", "assurance", "conditions", "decaisse"] as const;
function InsuranceCard({ stage }: { readonly stage: string }): React.JSX.Element | null {
  if (!(INSURANCE_CARD_FROM as readonly string[]).includes(stage)) return null;
  return (
    <section
      data-testid="dossier-assurance"
      className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
    >
      <h2 className="text-sm font-semibold">{t("console.dossier.assuranceTitre")}</h2>
      <p className="mt-2 text-sm text-gray-700" data-testid="dossier-assurance-aucune">
        {t("console.dossier.assuranceAucune")}
      </p>
      <p className="mt-1 text-xs text-gray-500" data-testid="dossier-assurance-client">
        {t("console.dossier.assuranceClient")}
      </p>
    </section>
  );
}

// ---------------------------------------------------------------- the wrapper

/**
 * `round K-1`, `M3`: « Où passe le temps » , the server's derivation
 * (`GET /dossiers/{id}/holds`), one line per owner with the days and the step,
 * a dash where the record gives the owner no step (R81), the total beneath.
 */
export interface HoldLedgerView {
  readonly hardenedAt: string;
  readonly totalDays: number;
  readonly pendingOwner: string;
  readonly buyerDays: number;
  readonly waitingOnBuyerDays: number | null;
  readonly owners: readonly {
    readonly owner: string;
    readonly party: string | null;
    readonly days: number;
    readonly step: string | null;
    readonly since: string | null;
  }[];
}

const OWNER_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["client", "console.dossier.tempsOwnerClient"],
  ["hakili", "console.dossier.tempsOwnerHakili"],
  ["banque", "console.dossier.tempsOwnerBanque"],
  ["expert", "console.dossier.tempsOwnerExpert"],
  ["assureur", "console.dossier.tempsOwnerAssureur"],
  ["concessionnaire", "console.dossier.tempsOwnerConcessionnaire"],
]);

/** The founder's five coded motives of a closure « sans suite » (WF-CON-19 amended), the wire's codes. */
const CLOSURE_CODE_WORDS: readonly { readonly code: string; readonly key: Parameters<typeof t>[0] }[] = [
  { code: "clientInjoignable", key: "console.dossier.cloreCodeClientInjoignable" },
  { code: "clientChangeAvis", key: "console.dossier.cloreCodeClientChangeAvis" },
  { code: "vehiculeAcheteAilleurs", key: "console.dossier.cloreCodeVehiculeAcheteAilleurs" },
  { code: "financementObtenuAilleurs", key: "console.dossier.cloreCodeFinancementObtenuAilleurs" },
  { code: "autre", key: "console.dossier.cloreCodeAutre" },
];

function HoldLedgerCard(props: {
  readonly holds: HoldLedgerView | null | undefined;
  /** `T-2b`, `M0 (c)`: a closed or ended file , the ledger answers null because the clock stopped, not because no lock stood. */
  readonly closed?: boolean;
}): React.JSX.Element | null {
  const holds = props.holds;
  if (holds === undefined) return null;
  return (
    <section data-testid="dossier-temps" className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <h2 className="text-sm font-semibold">{t("console.dossier.tempsTitre")}</h2>
      <p className="mt-0.5 text-sm text-gray-500">{t("console.dossier.tempsSous")}</p>
      {holds === null && props.closed === true ? (
        <p data-testid="dossier-temps-clos" className="mt-3 text-sm text-gray-500">
          {t("console.dossier.tempsClos")}
        </p>
      ) : holds === null ? (
        <p data-testid="dossier-temps-absent" className="mt-3 text-sm text-gray-500">
          {t("console.dossier.tempsAbsent")}
        </p>
      ) : (
        <>
          <ul className="mt-3 divide-y divide-gray-50">
            {holds.owners.map((line) => (
              <li
                key={`${line.owner}:${line.party ?? ""}`}
                data-testid={`dossier-temps-${line.owner}`}
                className="flex items-start justify-between gap-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {line.party ?? t(OWNER_WORDS.get(line.owner) ?? "console.dossier.tempsOwnerHakili")}
                    {line.since === null ? null : (
                      <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                        {t("console.dossier.tempsEnCours")}
                      </span>
                    )}
                  </p>
                  <p data-testid="dossier-temps-etape" className="text-xs text-gray-500">
                    {line.step ?? t("common.valeur.tiret")}
                  </p>
                </div>
                <p className="shrink-0 text-sm tabular-nums text-gray-700">
                  {t("console.dossier.tempsJours").replace("{n}", String(line.days))}
                </p>
              </li>
            ))}
          </ul>
          <p data-testid="dossier-temps-total" className="mt-3 text-sm text-gray-500">
            {t("console.dossier.tempsTotal")
              .replace("{n}", String(holds.totalDays))
              .replace("{date}", formatDate(new Date(holds.hardenedAt)))}
          </p>
        </>
      )}
    </section>
  );
}

export interface DossierConditionView {
  readonly key: string;
  readonly label: string;
  readonly observed: boolean;
  readonly satisfied: boolean;
  readonly satisfiedAt: string | null;
}

export interface BankConditionsView {
  readonly bankName: string;
  readonly conditions: readonly {
    readonly key: string;
    readonly label: string;
    readonly state: string;
    readonly liftedAt: string | null;
    /** Who observes it (typed by the API): « hakili » constates the fee itself; absent from an older read. */
    readonly liftedBy?: "banque" | "hakili";
  }[];
  readonly liftedCount: number;
  readonly total: number;
}

export interface DossierDoors {
  /** `P1-CNW-064` (D-1031-18): the dealer-access facts of a dossier, read only; handed to the seats that hold `voirEtRelancerDossiers`, `undefined` for the others. */
  readonly readDealerAccessFacts?: ((dossierId: string) => Promise<DealerAccessFactsAnswer>) | undefined;
  readonly readDossier: (id: string) => Promise<{
    readonly dossier: DossierDetail;
    readonly actions: readonly string[];
    readonly fee: FeeView | null;
    readonly verdict: string | null;
    readonly report?: ReportProvenance | null;
    readonly filterFailure?: string | null;
    readonly filterComputation?: { readonly eligibleCount: number } | null;
    readonly stagesBypassed?: readonly string[];
    readonly conditions?: readonly DossierConditionView[] | null;
    readonly bankConditions?: BankConditionsView | null;
    readonly settlement?: SettlementLineView | null;
  }>;
  /** `T-1b`, `M2`: the chargée's tick and its undoing, `POST /dossiers/{id}/conditions/{cle}/lift|reopen`. */
  readonly liftCondition?: (id: string, cle: string, how: "lift" | "reopen") => Promise<void>;
  readonly readJournal: (id: string) => Promise<readonly JournalLine[]>;
  /** `L-1b`, `M5` (b): the banks' answers on a submitted file. */
  readonly readSubmissions?: (id: string) => Promise<SubmissionsRead>;
  /** Round `L-2`, `M3` (B10): a bank's offer recorded by hand, the bank's own figures, `POST /submissions/{id}/offer`. */
  readonly recordOffer?: (submissionId: string, offer: RecordedOfferInput) => Promise<void>;
  /** Round `L-3`, `M2` (`P1-LND-014`): the chargé resubmits after a completion, `POST /submissions/{id}/resubmit`. */
  readonly resubmit?: (submissionId: string) => Promise<void>;
  /** Round `L-3b`, `M2` (M0 (d)): the refusal recorded by hand with its motive, `POST /submissions/{id}/refusal`. */
  readonly recordRefusal?: (submissionId: string, reasonCode: string) => Promise<void>;
  /** Round `L-3b`, `M2` (`P1-LND-015`): the declaration, `POST /dossiers/{id}/all-refused`. */
  readonly allRefused?: (id: string) => Promise<void>;
  /**
   * `FX-3`, `M2 (d)` (`D6`): « Signaler au contrôle », `POST /dossiers/{id}/fees/verification/flag-control`
   * , the chargé's ping to the seats that hold row 28. It confirms nothing and returns nothing but the
   * file's reference.
   */
  readonly flagVerificationTransfer?: (id: string) => Promise<void>;
  /** Round `L-3`, `M2`: the relance from a bank's return, the same door with its context. */
  readonly chaseForBank?: (id: string, documentCode: string) => Promise<{ readonly link: string | null }>;
  /** Round `C6`, `M4`: the report of record read, `GET /dossiers/{id}/rapport`; the chargé's request, `POST /expertise/requests/{id}/correction-request`. */
  readonly readReport: (id: string) => Promise<ReportReading | null>;
  readonly requestCorrection: (demandId: string, reason: string) => Promise<void>;
  /** `LANE-E`, `M0` (b): `POST /dossiers/{id}/rapport/lecture-hakili`, the report's missing sheet derived again. */
  readonly regenerateSheet: (dossierId: string) => Promise<void>;
  readonly readPieces: (
    id: string,
  ) => Promise<{ readonly pieces: readonly PieceView[]; readonly manquants: number }>;
  readonly readDemandFor: (dossierId: string) => Promise<string | null>;
  readonly readGrid: () => Promise<readonly GridFamily[]>;
  readonly readAssignable: () => Promise<readonly { id: string; fullName: string }[]>;
  readonly advanceDossier: (id: string, action: string, reason?: string) => Promise<void>;
  readonly closeDossier: (id: string, reason: string, code: string) => Promise<void>;
  /** `A-RECOLLECTE`: `POST /dossiers/{id}/remettre-en-collecte`. */
  readonly returnToCollecte?: ((id: string, reason: string, ask?: ReturnAsk) => Promise<void>) | undefined;
  /** `K-1`, `M3`: the ledger of days on a held file, `GET /dossiers/{id}/holds`. */
  readonly readHolds?: (id: string) => Promise<HoldLedgerView | null>;
  readonly reassignDossier: (id: string, userId: string, reason: string) => Promise<void>;
  /** `round N+1`: no `author` parameter , the staff name is resolved server side, never sent. */
  readonly fileOnBehalf: (
    id: string,
    documentCode: string,
    channel: string,
    note: string,
    files: readonly File[],
    /** `PIECES-COUNT-ENFORCE`: the months each file holds, by file, for a multi-file pièce; absent for every other pièce (the door reads 1). */
    declaredMonths?: readonly number[],
  ) => Promise<void>;
  readonly fileReport: (demandId: string, entry: ReportEntry) => Promise<void>;
  /** `C7.3`: the chase mints a link for ONE pièce, answered once as a PATH on the client app. */
  readonly chaseDocument: (id: string, documentCode: string) => Promise<{ readonly link: string | null }>;
  /** `S4-6`: the mirror , the same shape, the vendor named instead of the client. */
  readonly chaseSellerDocument: (
    id: string,
    documentCode: string,
  ) => Promise<{ readonly link: string | null }>;
  readonly chaseAll: (
    id: string,
  ) => Promise<readonly { readonly documentCode: string; readonly link: string | null }[]>;
  readonly rejectDocument: (id: string, documentCode: string, reason: string) => Promise<void>;
  /** `VISITE-SANS-OBJET`: `POST …/pieces/visiteTechnique/sans-objet` and `…/sans-objet/undo`, each with its reason. */
  readonly markNotApplicable: (id: string, documentCode: string, reason: string) => Promise<void>;
  readonly undoNotApplicable: (id: string, documentCode: string, reason: string) => Promise<void>;
  readonly verifyIdentity: (id: string, gender: "f" | "m", holderName: string) => Promise<void>;
  /** `S4-9`: opens a pièce , a presigned GET, `POST …/pieces/{piece}/consult`. */
  readonly consultDocument: (id: string, documentCode: string) => Promise<{ url: string | null }>;
  /** `U-1`, `M3`: « Lecture de Hakili », `GET /dossiers/{id}/first-pass`; the two acts, `POST …/first-pass/fields/{field}/confirm|correct`. */
  readonly readFirstPass?: (id: string) => Promise<FirstPassReadingView | null>;
  /** `U-3`, `M1`: `GET /dossiers/{id}/synthesis`. */
  readonly readSynthesis?: (id: string) => Promise<SynthesisView | null>;
  readonly actOnFirstPassField?: (
    id: string,
    key: string,
    act: "confirmee" | "corrigee" | "manuelle",
    value: string | null,
  ) => Promise<void>;
  /** Round 11a: `GET /dossiers/{id}/underwriting` , the console run's analysis. */
  readonly readUnderwriting?: (id: string) => Promise<ConsoleUnderwritingView | null>;
  /**
   * `AA-3c`: `GET /dossiers/{id}/analysis` , where the chain behind the one button stands, the button's own answer and the
   * folded history; `POST /dossiers/{id}/analysis/launch` , « Lancer l'analyse Hakili », the chargée's own act.
   */
  readonly readAnalysis?: (id: string) => Promise<AnalysisChainView | null>;
  readonly launchAnalysis?: (id: string) => Promise<void>;
  /** `AA-4b`: `GET /dossiers/{id}/submission-options` and the press, `POST /dossiers/{id}/submissions` with the banks she ticked. */
  readonly readSubmissionOptions?: (id: string) => Promise<SubmissionOptionsView | null>;
  readonly submitToBanks?: (id: string, bankIds: readonly string[]) => Promise<void>;
  /** Round 10a: `GET /dossiers/{id}/employment-verification` , the act that rules and the send gate's state. */
  readonly readEmploymentVerification?: (id: string) => Promise<EmploymentVerificationState>;
  /** Round 10a: `POST /dossiers/{id}/employment-verification` , the chargée's own act; the door's refusal is thrown in its words. */
  readonly recordEmploymentVerification?: (id: string, body: VerificationBody) => Promise<void>;
}

export interface DossierScreenProps extends DossierDoors {
  readonly vue: Vue;
  readonly meId: string;
  readonly dossierId: string;
  /** `C7.3`: the client app's origin the deposit link is absolute on. */
  readonly clientOrigin: string;
  readonly onBack: () => void;
  readonly onGoExpertises: () => void;
}

// Asked again after a later await: the narrowing from the first check is
// stale by then, and TS cannot see the suspension point.
const isLive = (run: { readonly live: boolean }): boolean => run.live;

const failureWords = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

/**
 * The layer that asks. Everything the page shows comes from a door; the
 * refusals it prints are the doors' own sentences (`4.5.5`), never this file's.
 * A gesture that changed the file re-reads it , the stage, the actions and the
 * fee are the DOOR's to say, and a screen that moved its own chip would be
 * guessing at a transition the guard may have refused.
 */
export function DossierScreen(props: DossierScreenProps): React.JSX.Element | null {
  const [state, setState] = useState<{
    readonly dossier: DossierDetail;
    readonly actions: readonly string[];
    readonly fee: FeeView | null;
    readonly verdict: string | null;
    readonly report?: ReportProvenance | null;
    readonly filterFailure?: string | null;
    readonly filterComputation?: { readonly eligibleCount: number } | null;
    readonly stagesBypassed?: readonly string[];
    readonly conditions?: readonly DossierConditionView[] | null;
    readonly bankConditions?: BankConditionsView | null;
    readonly settlement?: SettlementLineView | null;
    readonly holds?: HoldLedgerView | null;
    readonly reading: ReportReading | null;
    readonly pieces: readonly PieceView[];
    readonly manquants: number;
    readonly journal: readonly JournalLine[];
    readonly demandId: string | null;
    readonly submissions: readonly SubmissionLine[];
    readonly refusalReasons: readonly { readonly code: string; readonly label: string }[];
    readonly firstPass?: FirstPassReadingView | null | undefined;
    readonly synthesis?: SynthesisView | null | undefined;
  } | null>(null);
  const [underwriting, setUnderwriting] = useState<ConsoleUnderwritingView | null | undefined>(undefined);
  const [analysis, setAnalysis] = useState<AnalysisChainView | null | undefined>(undefined);
  const [submissionOptions, setSubmissionOptions] = useState<SubmissionOptionsView | null | undefined>(
    undefined,
  );
  const [employment, setEmployment] = useState<EmploymentVerificationState | null | undefined>(undefined);
  const [grid, setGrid] = useState<readonly GridFamily[] | null>(null);
  const [assignable, setAssignable] = useState<readonly { id: string; fullName: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // `COVERAGE-READ-CHECK`: the lines the door served for the short pièces of a refused send, until she returns the file or sends again.
  const [returnPrefill, setReturnPrefill] = useState<readonly ReturnPrefillLine[] | null>(null);
  const [sendRefusal, setSendRefusal] = useState<string | null>(null);
  // `PRET-SEND-FILTER-GUARD`: the door's sentence for a field act refused from `soumis` onward.
  const [fieldActRefusal, setFieldActRefusal] = useState<string | null>(null);
  // `READING-REQUIRED`: the door's sentence for « Valider et passer à Prêt » refused for a reading never launched.
  const [readingRefusal, setReadingRefusal] = useState<string | null>(null);
  /**
   * `round CN-H`, `M0(b)`: **a refusal on one PART renders where the part is,
   * and the page is never replaced whole.** Until this round one `try` wrapped
   * every read of the page , the file, the journal, the pièces, the queue,
   * the grid, the team , and a door refusing ANY of them threw the whole page
   * away for « Vous n'avez pas le droit d'effectuer cette action. » That is
   * the boundary that swallowed the chargé's own file on 13 September: his
   * file's door answered, the queue's (`saisirRapportExpertise`, row 18)
   * refused him, and the page showed him the refusal instead of his file.
   * The file's own read alone may empty the page; every other part carries
   * its own refusal, in its own words, where the part is drawn.
   */
  const [partRefusals, setPartRefusals] = useState<Readonly<Record<string, string>>>({});
  const [epoch, setEpoch] = useState(0);
  // `C7.3`: the links minted this sitting, absolute on the client app's origin.
  const [links, setLinks] = useState<ReadonlyMap<string, string>>(new Map());

  const {
    dossierId,
    vue,
    readDossier,
    readJournal,
    readSubmissions,
    readPieces,
    readDemandFor,
    readGrid,
    readAssignable,
    readReport,
    readHolds,
    readFirstPass,
    readAnalysis,
    readSubmissionOptions,
    readSynthesis,
    readUnderwriting,
    readEmploymentVerification,
  } = props;
  useEffect(() => {
    const run: { live: boolean } = { live: true };
    // A read again says what the doors say now: a part's earlier refusal does not outlive the read that replaces it (A47).
    setPartRefusals({});
    // Each part on its own: a part's refusal is recorded under its name and
    // rendered where the part is; only the file's own read empties the page.
    const part = async <T,>(name: string, read: () => Promise<T>, fallback: T): Promise<T> => {
      try {
        return await read();
      } catch (failure) {
        if (run.live) setPartRefusals((now) => ({ ...now, [name]: failureWords(failure) }));
        return fallback;
      }
    };
    void (async () => {
      let read: Awaited<ReturnType<typeof readDossier>>;
      try {
        read = await readDossier(dossierId);
      } catch (failure) {
        if (run.live) setRefusal(failureWords(failure));
        return;
      }
      const [journal, pieces, holds, firstPass, chain, synthesis, analysis, employment] = await Promise.all([
        part("journal", () => readJournal(dossierId), [] as readonly JournalLine[]),
        part("pieces", () => readPieces(dossierId), { pieces: [] as readonly PieceView[], manquants: 0 }),
        // `K-1`, `M3 (d)`: « Où passe le temps », its own part; `null` where the car is not held.
        readHolds === undefined
          ? Promise.resolve(null)
          : part("holds", () => readHolds(dossierId), null as HoldLedgerView | null),
        // `U-1`, `M3`: « Lecture de Hakili », its own part; `undefined` where the door was not handed.
        readFirstPass === undefined
          ? Promise.resolve(undefined)
          : part("lecture", () => readFirstPass(dossierId), null as FirstPassReadingView | null),
        // `AA-3c`: where the chain behind the one button stands, its own part , a refusal here greys nothing else.
        readAnalysis === undefined
          ? Promise.resolve(undefined)
          : part("chaine", () => readAnalysis(dossierId), null as AnalysisChainView | null),
        // `U-3`, `M1`: the synthesis, its own part.
        readSynthesis === undefined
          ? Promise.resolve(undefined)
          : part("synthese", () => readSynthesis(dossierId), null as SynthesisView | null),
        // Round 11a: the console run's analysis, its own part; `undefined` where the door was not handed.
        readUnderwriting === undefined
          ? Promise.resolve(undefined)
          : part("analyse", () => readUnderwriting(dossierId), null as ConsoleUnderwritingView | null),
        // Round 10a: the employment verification and the send gate, its own part; `undefined` where the door was not handed.
        readEmploymentVerification === undefined
          ? Promise.resolve(undefined)
          : part(
              "verification",
              () => readEmploymentVerification(dossierId),
              null as EmploymentVerificationState | null,
            ),
      ]);
      // Round `C6`, `M4`: the report of record, its own part , absent, a file without a report.
      const reading =
        read.report === undefined || read.report === null
          ? null
          : await part("rapport", () => readReport(dossierId), null as ReportReading | null);
      // The demand and the grid matter only at `expertise`, and the doors that
      // answer them are the RESPONSABLE's (`06` row 18): a chargé's screen never
      // asks them, so his own file is never refused on a question that is not his.
      const mayReport = vue === "admin" || vue === "resp";
      const demandId =
        read.dossier.stage === "expertise" && mayReport
          ? await part("demand", () => readDemandFor(dossierId), null)
          : null;
      // `L-1b`, `M5` (b): the banks' answers, asked at `soumis` , its own part; `L-4`, `M2`: and on
      // the terminal stages a submitted file reaches, so the closed file keeps its board (B14).
      const board =
        (read.dossier.stage === "soumis" ||
          read.dossier.stage === "sansSuite" ||
          read.dossier.stage === "refuseBanque") &&
        readSubmissions !== undefined
          ? await part("soumissions", () => readSubmissions(dossierId), {
              submissions: [] as readonly SubmissionLine[],
              refusalReasons: [] as readonly { code: string; label: string }[],
            })
          : {
              submissions: [] as readonly SubmissionLine[],
              refusalReasons: [] as readonly { code: string; label: string }[],
            };
      // `AA-4b`: the banks to choose from, asked at `pret` , its own part.
      const options =
        read.dossier.stage === "pret" && readSubmissionOptions !== undefined
          ? await part(
              "soumissionBanques",
              () => readSubmissionOptions(dossierId),
              null as SubmissionOptionsView | null,
            )
          : null;
      if (!run.live) return;
      setSubmissionOptions(options);
      setUnderwriting(analysis);
      setEmployment(employment);
      setAnalysis(chain);
      setState({
        ...read,
        ...pieces,
        holds,
        journal,
        demandId,
        reading,
        submissions: board.submissions,
        refusalReasons: board.refusalReasons,
        ...(firstPass === undefined ? {} : { firstPass }),
        ...(synthesis === undefined ? {} : { synthesis }),
      });
      if (read.dossier.stage === "expertise" && demandId !== null) {
        const families = await part("grid", () => readGrid(), null as readonly GridFamily[] | null);
        if (isLive(run)) setGrid(families);
      }
      if (mayReport) {
        const charges = await part(
          "assignable",
          () => readAssignable(),
          [] as readonly { id: string; fullName: string }[],
        );
        if (isLive(run)) setAssignable(charges);
      }
    })();
    return () => {
      run.live = false;
    };
  }, [
    dossierId,
    vue,
    readDossier,
    readJournal,
    readPieces,
    readDemandFor,
    readGrid,
    readAssignable,
    readFirstPass,
    readAnalysis,
    readSubmissionOptions,
    readSynthesis,
    readUnderwriting,
    readEmploymentVerification,
    epoch,
  ]);

  // `AA-4a`: ONE poller reads the screen again by itself while anything moves: the reading under way, the console run in
  // flight, the chain at its reading or its analysis (the filter and the banks are the tail of the analysis).
  const readingStatus = state?.firstPass?.run.status;
  useAnalysisPolling({
    dossierId,
    moving:
      readingStatus === "en_attente" ||
      readingStatus === "en_cours" ||
      (underwriting !== undefined && underwriting !== null && underwriting.run.status !== "terminee") ||
      analysis?.chain?.state === "lecture" ||
      analysis?.chain?.state === "analyse",
    readFirstPass,
    readAnalysis,
    readUnderwriting,
    onRead: (snapshot) => {
      if (snapshot.reading !== undefined) {
        const { reading } = snapshot;
        setState((now) => (now === null ? now : { ...now, firstPass: reading }));
      }
      if (snapshot.analysis !== undefined) setAnalysis(snapshot.analysis);
      if (snapshot.underwriting !== undefined) setUnderwriting(snapshot.underwriting);
    },
  });

  if (state === null) {
    return refusal === null ? null : (
      <p role="alert" data-testid="dossier-refus" className="text-sm text-rose-600">
        {refusal}
      </p>
    );
  }

  // `onFailure` answers true where the gesture draws its refusal in its own place, so the shared line under « Sortie du dossier » stays empty.
  const act = (gesture: () => Promise<void>, onFailure?: (failure: unknown) => boolean): void => {
    void (async () => {
      setBusy(true);
      setRefusal(null);
      try {
        await gesture();
        setEpoch((now) => now + 1);
      } catch (failure) {
        if (onFailure?.(failure) !== true) setRefusal(failureWords(failure));
        // A `409` is the STATE refusing the act (a guard the page's own read did not see: a computation withdrawn, a stage moved): read the file again, so what is
        // greyed or offered is the door's answer now and never a stale one (A47). The refusal's words stay printed.
        if ((failure as { status?: unknown } | null)?.status === 409) setEpoch((now) => now + 1);
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Dossier
      vue={vue}
      {...(props.readDealerAccessFacts === undefined
        ? {}
        : { dealerAccess: { dossierId, read: props.readDealerAccessFacts } })}
      meId={props.meId}
      dossier={state.dossier}
      actions={state.actions}
      fee={state.fee}
      filterFailure={state.filterFailure ?? null}
      filterComputation={state.filterComputation ?? null}
      stagesBypassed={state.stagesBypassed ?? []}
      holds={state.holds}
      conditions={state.conditions ?? null}
      bankConditions={state.bankConditions ?? null}
      settlement={state.settlement ?? null}
      onLiftCondition={
        props.liftCondition === undefined
          ? undefined
          : (cle, how) => {
              act(() => props.liftCondition?.(dossierId, cle, how) ?? Promise.resolve());
            }
      }
      verdict={state.verdict}
      firstPass={state.firstPass}
      synthesis={state.synthesis}
      underwriting={underwriting}
      verification={employment}
      onRecordVerification={
        props.recordEmploymentVerification === undefined
          ? undefined
          : async (body) => {
              // Not `act()`: her refusal is printed inside the form, in the door's words, and the file is re-read after.
              await props.recordEmploymentVerification?.(dossierId, body);
              setEpoch((now) => now + 1);
            }
      }
      submissionOptions={submissionOptions}
      returnPrefill={returnPrefill}
      sendRefusal={sendRefusal}
      fieldActRefusal={fieldActRefusal}
      onSubmitToBanks={
        props.submitToBanks === undefined
          ? undefined
          : (bankIds) => {
              setReturnPrefill(null);
              setSendRefusal(null);
              act(
                () => props.submitToBanks?.(dossierId, bankIds) ?? Promise.resolve(),
                // `COVERAGE-READ-CHECK` (`P0-DOS-066`): the door's refusal for a multi-file pièce read short of its months carries, per short pièce,
                // the sentence the CLIENT reads. The return form opens with them; nothing is composed here from the months.
                (failure) => {
                  const served = coverageGateDetailsSchema.safeParse(
                    (failure as { details?: unknown } | null)?.details,
                  );
                  // `PRET-SEND-FILTER-GUARD` (`P0-DOS-074`): the kept filter computation was withdrawn; the door names the guard and the console says its own words.
                  if (
                    (failure as { details?: { guard?: unknown } } | null)?.details?.guard === "filtre_calcule"
                  ) {
                    setSendRefusal(t("console.dossier.validerNonCalcule"));
                    return true;
                  }
                  if (!served.success) return false;
                  setReturnPrefill(
                    served.data.pieces.map((one) => ({ pieceCode: one.code, text: one.clientSentence })),
                  );
                  setSendRefusal(failureWords(failure));
                  return true;
                },
              );
            }
      }
      analysis={analysis}
      onLaunchAnalysis={
        props.launchAnalysis === undefined
          ? undefined
          : () => {
              act(() => props.launchAnalysis?.(dossierId) ?? Promise.resolve());
            }
      }
      onFirstPassAct={
        props.actOnFirstPassField === undefined
          ? undefined
          : (key, actOn, value) => {
              setFieldActRefusal(null);
              act(
                () => props.actOnFirstPassField?.(dossierId, key, actOn, value) ?? Promise.resolve(),
                // `P0-DOS-074`: the reading is frozen from `soumis` onward; the door names the guard and its sentence is drawn as served.
                (failure) => {
                  const details = (failure as { details?: { guard?: unknown } } | null)?.details;
                  if (details?.guard !== "lecture_figee") return false;
                  setFieldActRefusal(failureWords(failure));
                  return true;
                },
              );
            }
      }
      onFirstPassEnter={
        props.actOnFirstPassField === undefined
          ? undefined
          : async (entries) => {
              const refused: Record<string, string> = {};
              for (const entry of entries) {
                try {
                  await props.actOnFirstPassField?.(dossierId, entry.key, "manuelle", entry.value);
                } catch (failure) {
                  refused[entry.key] = failureWords(failure);
                }
              }
              setEpoch((now) => now + 1);
              return refused;
            }
      }
      pieces={state.pieces}
      submissions={state.submissions}
      refusalReasons={state.refusalReasons}
      recordOffer={props.recordOffer}
      recordRefusal={props.recordRefusal}
      onRecorded={() => {
        setEpoch((now) => now + 1);
      }}
      onAllRefused={
        props.allRefused === undefined
          ? undefined
          : () => {
              act(() => props.allRefused?.(dossierId) ?? Promise.resolve());
            }
      }
      onChaseForBank={
        props.chaseForBank === undefined
          ? undefined
          : (documentCode) => {
              act(async () => {
                const answer = await props.chaseForBank?.(dossierId, documentCode);
                if (answer !== undefined && answer.link !== null) {
                  const absolute = `${props.clientOrigin}${answer.link}`;
                  setLinks((now) => new Map([...now, [documentCode, absolute]]));
                }
              });
            }
      }
      onResubmit={
        props.resubmit === undefined
          ? undefined
          : (submissionId) => {
              act(() => props.resubmit?.(submissionId) ?? Promise.resolve());
            }
      }
      manquants={state.manquants}
      journal={state.journal}
      demandId={state.demandId}
      grid={grid}
      assignable={assignable}
      busy={busy}
      refusal={refusal}
      partRefusals={partRefusals}
      onBack={props.onBack}
      onGoExpertises={props.onGoExpertises}
      {...(props.flagVerificationTransfer === undefined
        ? {}
        : {
            // `FX-3`, `M2 (d)`: the panel reports « signalé » only when the door answered. A button that
            // said so on its own click would tell a chargé that finance has been pinged when nothing left.
            flagControl: async (): Promise<boolean> => {
              try {
                await props.flagVerificationTransfer?.(dossierId);
                return true;
              } catch {
                return false;
              }
            },
          })}
      readingRefusal={readingRefusal}
      onRetryRead={() => {
        setEpoch((now) => now + 1);
      }}
      onAdvance={(action) => {
        setReadingRefusal(null);
        act(
          () => props.advanceDossier(dossierId, action),
          // `READING-REQUIRED` (RR-2): the gate names itself (`details.guard`); its sentence is drawn beside the gesture, as served.
          (failure) => {
            const details = (failure as { details?: { guard?: unknown } } | null)?.details;
            if (details?.guard !== "reading_not_launched") return false;
            setReadingRefusal(failureWords(failure));
            return true;
          },
        );
      }}
      onRefuse={(reason) => {
        act(() => props.advanceDossier(dossierId, ACTION.rejectAtFilter, reason));
      }}
      {...(props.returnToCollecte === undefined
        ? {}
        : {
            onReturnToCollecte: (reason: string, ask?: ReturnAsk) => {
              setReturnPrefill(null);
              setSendRefusal(null);
              act(() => props.returnToCollecte?.(dossierId, reason, ask) ?? Promise.resolve());
            },
          })}
      onClose={(reason, code) => {
        act(() => props.closeDossier(dossierId, reason, code));
      }}
      onReassign={(userId, reason) => {
        act(() => props.reassignDossier(dossierId, userId, reason));
      }}
      onFileOnBehalf={(documentCode, channel, note, files, declaredMonths) => {
        act(() => props.fileOnBehalf(dossierId, documentCode, channel, note, files, declaredMonths));
      }}
      onFileReport={(entry) => {
        const demand = state.demandId;
        if (demand === null) return;
        act(() => props.fileReport(demand, entry));
      }}
      reading={state.reading}
      onRequestCorrection={(reason) => {
        const demand = state.reading?.demandId ?? state.demandId;
        if (demand === null) return;
        act(() => props.requestCorrection(demand, reason));
      }}
      onRegenerateSheet={() => {
        act(() => props.regenerateSheet(dossierId));
      }}
      links={links}
      onChase={(documentCode) => {
        act(async () => {
          const answer = await props.chaseDocument(dossierId, documentCode);
          if (answer.link !== null) {
            const absolute = `${props.clientOrigin}${answer.link}`;
            setLinks((now) => new Map([...now, [documentCode, absolute]]));
          }
        });
      }}
      onChaseSeller={(documentCode) => {
        act(async () => {
          const answer = await props.chaseSellerDocument(dossierId, documentCode);
          if (answer.link !== null) {
            const absolute = `${props.clientOrigin}${answer.link}`;
            setLinks((now) => new Map([...now, [documentCode, absolute]]));
          }
        });
      }}
      onChaseAll={() => {
        act(async () => {
          const minted = await props.chaseAll(dossierId);
          setLinks((now) => {
            const next = new Map(now);
            for (const one of minted) {
              if (one.link !== null) next.set(one.documentCode, `${props.clientOrigin}${one.link}`);
            }
            return next;
          });
        });
      }}
      onReject={(documentCode, reason) => {
        act(() => props.rejectDocument(dossierId, documentCode, reason));
      }}
      onMarkNotApplicable={(documentCode, reason) => {
        act(() => props.markNotApplicable(dossierId, documentCode, reason));
      }}
      onUndoNotApplicable={(documentCode, reason) => {
        act(() => props.undoNotApplicable(dossierId, documentCode, reason));
      }}
      onVerifyIdentity={(gender, holderName) => {
        act(() => props.verifyIdentity(dossierId, gender, holderName));
      }}
      onConsult={async (documentCode) => {
        try {
          const { url } = await props.consultDocument(dossierId, documentCode);
          // The moment is now stamped on the server; re-read so civilité unlocks.
          setEpoch((now) => now + 1);
          return url;
        } catch (failure) {
          setRefusal(failureWords(failure));
          return null;
        }
      }}
    />
  );
}

/** `U-3`, `M1`; `P45`: the synthesis' counters and points, each tone a dot, no box. */
function SynthesisBlock(props: {
  readonly synthesis: SynthesisView;
  readonly onOpenConversation: () => void;
}): React.JSX.Element {
  const { counters, points } = props.synthesis;
  const dot = (value: SynthesisView["points"][number]["tone"]): string =>
    value === "alerte"
      ? "bg-rose-500"
      : value === "attention"
        ? "bg-amber-500"
        : value === "ok"
          ? "bg-emerald-500"
          : "bg-gray-400";
  return (
    <div data-testid="dossier-synthese-derivee" data-rule={props.synthesis.rule}>
      <dl className="mt-3 grid grid-cols-4 gap-2 text-xs" data-testid="dossier-synthese-compteurs">
        <div>
          <dt className="text-gray-500">{t("console.dossier.syntheseRelances")}</dt>
          <dd className="font-medium text-gray-900">{counters.relances}</dd>
        </div>
        <div>
          <dt className="text-gray-500">{t("console.dossier.syntheseDernierContact")}</dt>
          <dd className="font-medium text-gray-900">
            {counters.lastContactOn === null ? DASH : formatDate(new Date(counters.lastContactOn))}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">{t("console.dossier.syntheseCanal")}</dt>
          <dd className="font-medium text-gray-900">
            {counters.preferredChannel === null ? DASH : channelWord(counters.preferredChannel)}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">{t("console.dossier.syntheseMessages")}</dt>
          <dd className="font-medium">
            <button
              type="button"
              data-testid="dossier-synthese-messages"
              onClick={props.onOpenConversation}
              className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
            >
              <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
              {counters.messages}
            </button>
          </dd>
        </div>
      </dl>
      {points.length === 0 ? null : (
        <>
          <p className="mt-3 text-xs font-semibold text-gray-700">{t("console.dossier.syntheseASavoir")}</p>
          <ul className="mt-1.5 space-y-1.5" data-testid="dossier-synthese-points">
            {points.map((point, index) => (
              <li
                key={index}
                className="flex items-start gap-2 text-sm text-gray-700"
                data-testid={`dossier-synthese-point-${String(index + 1)}`}
                data-tone={point.tone}
              >
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot(point.tone)}`}
                  aria-hidden="true"
                />
                {point.text}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
